import { useEffect, useMemo, useState } from 'react'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DownloadIcon from '@mui/icons-material/Download'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  LinearProgress,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom'
import { workspaceApi } from '../api/workspaces'
import { workspaceFlowsApi, type QuestionnaireAnswers, type QuestionnaireQuestion, type IncomeBucket, type WorkspaceAdjustments, type ReviewPayload, type AuditResult, type ReviewEvidenceItem, type WorkspaceDocument, type EvidenceLinkOption, type DocumentEvidenceLinksPayload } from '../api/workspaceFlows'
import { useAuth } from '../auth/useAuth'
import {
  describeAutomaticEvidenceMapping,
  describeEvidenceLinkSelection,
  findEvidenceForDocument,
  getEvidenceLinkOptionValue,
  getIr3FieldEvidenceLabel,
  getSelectedEvidenceLinkValues,
  getWarningEvidenceLabel,
  normalizeEvidenceLinkSelection,
  resolveEvidenceLinkPayload,
} from './workspaceEvidence'

function formatDate(value?: string) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString()
}

function formatBalanceDateFromTaxYearEnd(value?: string) {
  if (!value) return ''
  const [year, month, day] = value.split('-')
  if (!year || !month || !day) return ''
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return ''
  const monthLabel = date.toLocaleString(undefined, { month: 'long' })
  return `${Number(day)} ${monthLabel} ${year}`
}

function percent(done: number, total: number) {
  if (!total) return 0
  return Math.round((done / total) * 100)
}

function isAnsweredQuestionValue(value: unknown) {
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return value.length > 0
  return value !== undefined && value !== null
}

function titleCase(value?: string | null) {
  if (!value) return 'Unknown'
  return value
    .split(/[_\-.\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function formatFieldValue(value: unknown) {
  if (typeof value === 'number') return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return String(value)
}

function blockerTargetTabIndex(targetTab?: 'questionnaire' | 'documents' | 'ir3_summary') {
  if (targetTab === 'documents') return 3
  if (targetTab === 'ir3_summary') return 4
  return 0
}

function reviewerActionSupportStateLabel(value?: 'missing_support' | 'missing_input' | 'review_required' | 'assumed') {
  if (value === 'missing_support') return 'Missing support'
  if (value === 'missing_input') return 'Missing input'
  if (value === 'assumed') return 'Assumed'
  return 'Review required'
}

function reviewerActionTypeLabel(value?: 'collect_document' | 'complete_input' | 'review_tax_position' | 'replace_assumption') {
  if (value === 'collect_document') return 'Collect document'
  if (value === 'complete_input') return 'Complete input'
  if (value === 'replace_assumption') return 'Replace assumption'
  return 'Review tax position'
}

function filingCheckpointStatusLabel(value?: 'not_started' | 'submitted' | 'confirmed') {
  if (value === 'submitted') return 'Submitted'
  if (value === 'confirmed') return 'Confirmed'
  return 'Not started'
}

function isEvidenceGapTitle(value?: string | null) {
  return String(value || '').startsWith('Evidence gap')
}

const sectionGuidance: Record<string, string> = {
  'About you': 'Start with the basics so your accountant knows whose return this is and what kind of help you need.',
  'Money you received': 'Add the kinds of income or documents most people are asked for, even if you think IRD already has some of it.',
  'Property, investments, and assets': 'Use this section for rentals, property sales, mortgage interest, and larger assets that may affect tax.',
  'Business, overseas, and special situations': 'Use this for self-employed work, trusts, overseas income, and anything that feels more complex than normal salary or bank interest.',
  'Family, children, and tax credits': 'Use this if children, Working for Families, partner income, or family changes might affect the return.',
  'Family and credits': 'Use this if children, Working for Families, partner income, or family changes might affect the return.',
  'Working from home': 'Only fill this in if you used part of your home mainly for work, storage, or a workshop.',
}

const familyBranchGroups = [
  {
    key: 'family-core',
    title: 'Core family details',
    detail: 'Start with the people and income details that usually drive the rest of this branch.',
    questionIds: ['children_details', 'child_support_or_maintenance_details', 'partner_income_details'],
  },
  {
    key: 'family-payments',
    title: 'Credits already received',
    detail: 'Only answer these if IRD already paid Working for Families amounts during the year.',
    questionIds: ['received_working_for_families'],
  },
  {
    key: 'family-changes',
    title: 'Changes and care arrangements',
    detail: 'Only fill in the items that changed during the year or affect shared care and hours tests.',
    questionIds: ['family_circumstances_changed', 'shared_child_custody', 'worked_under_hours_threshold'],
  },
  {
    key: 'family-special-income',
    title: 'Special income checks',
    detail: 'This is the extra Working for Families checklist. Most users will leave this section off.',
    questionIds: ['has_wff_additional_income_items'],
  },
] as const

const bodyFont = 'Source Sans 3, Inter, sans-serif'

export default function WorkspaceDetail() {
  const { workspaceId } = useParams<{ workspaceId: string }>()
  const queryClient = useQueryClient()
  const { getUserEmail, isAuthenticated, isLoading } = useAuth()
  const [tab, setTab] = useState(0)
  const [incomeType, setIncomeType] = useState<'paye' | 'interest' | 'dividends' | 'other'>('paye')
  const [gross, setGross] = useState('')
  const [payeWithheld, setPayeWithheld] = useState('')
  const [incomeAmount, setIncomeAmount] = useState('')
  const [incomeSourceName, setIncomeSourceName] = useState('')
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null)
  const [questionnaireDraftAnswers, setQuestionnaireDraftAnswers] = useState<QuestionnaireAnswers>({})
  const [questionnaireVisibleQuestions, setQuestionnaireVisibleQuestions] = useState<QuestionnaireQuestion[]>([])
  const [questionTextDrafts, setQuestionTextDrafts] = useState<Record<string, string>>({})
  const [csvText, setCsvText] = useState('date,asset,type,amount,price_nzd,fee_nzd,exchange\n2025-06-01,BTC,buy,0.01,100000,15,Binance')
  const [docType, setDocType] = useState('')
  const [docFile, setDocFile] = useState<File | null>(null)
  const [donationReceiptAmount, setDonationReceiptAmount] = useState('0')
  const [donationAmount, setDonationAmount] = useState('0')
  const [pieIncome, setPieIncome] = useState('0')
  const [pieTaxCredits, setPieTaxCredits] = useState('0')
  const [extraTaxDeducted, setExtraTaxDeducted] = useState('0')
  const [studentLoanRepayments, setStudentLoanRepayments] = useState('0')
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null)
  const [accountantPackFeedback, setAccountantPackFeedback] = useState<string | null>(null)
  const [isDownloadingAccountantPack, setIsDownloadingAccountantPack] = useState(false)
  const [auditCategory, setAuditCategory] = useState<string>('all')
  const [auditSearch, setAuditSearch] = useState('')

  const workspaceQuery = useQuery({
    queryKey: ['workspace', workspaceId],
    queryFn: () => workspaceApi.get(workspaceId || ''),
    enabled: Boolean(workspaceId),
  })
  const userEmail = getUserEmail()
  const defaultBalanceDate = useMemo(() => formatBalanceDateFromTaxYearEnd(workspaceQuery.data?.taxYearEnd), [workspaceQuery.data?.taxYearEnd])

  const questionnaireQuery = useQuery({
    queryKey: ['workspace-questionnaire', workspaceId],
    queryFn: () => workspaceFlowsApi.getQuestionnaire(workspaceId || ''),
    enabled: Boolean(workspaceId),
  })

  const questionnaireCacheKey = useMemo(() => (workspaceId ? `workspace-questionnaire:${workspaceId}` : null), [workspaceId])

  const incomeQuery = useQuery({
    queryKey: ['workspace-income', workspaceId],
    queryFn: () => workspaceFlowsApi.listIncome(workspaceId || ''),
    enabled: Boolean(workspaceId),
  })

  const cryptoQuery = useQuery({
    queryKey: ['workspace-crypto', workspaceId],
    queryFn: () => workspaceFlowsApi.listCryptoTransactions(workspaceId || ''),
    enabled: Boolean(workspaceId),
  })

  const docsQuery = useQuery({
    queryKey: ['workspace-docs', workspaceId],
    queryFn: () => workspaceFlowsApi.listDocuments(workspaceId || ''),
    enabled: Boolean(workspaceId),
  })

  const checklistQuery = useQuery({
    queryKey: ['workspace-checklist', workspaceId],
    queryFn: () => workspaceFlowsApi.getChecklist(workspaceId || ''),
    enabled: Boolean(workspaceId),
  })

  const auditQuery = useQuery({
    queryKey: ['workspace-audit', workspaceId, auditCategory, auditSearch],
    queryFn: () => workspaceFlowsApi.getAudit(workspaceId || '', {
      category: auditCategory === 'all' ? null : auditCategory,
      q: auditSearch.trim() || null,
    }),
    enabled: Boolean(workspaceId),
  })

  const calcQuery = useQuery({
    queryKey: ['workspace-calc', workspaceId],
    queryFn: () => workspaceFlowsApi.getIr3Calc(workspaceId || ''),
    enabled: Boolean(workspaceId),
  })

  const exportQuery = useQuery({
    queryKey: ['workspace-export', workspaceId],
    queryFn: () => workspaceFlowsApi.getDraftExport(workspaceId || ''),
    enabled: Boolean(workspaceId),
  })

  const adjustmentsQuery = useQuery({
    queryKey: ['workspace-adjustments', workspaceId],
    queryFn: () => workspaceFlowsApi.getAdjustments(workspaceId || ''),
    enabled: Boolean(workspaceId),
  })

  const reviewQuery = useQuery({
    queryKey: ['workspace-review', workspaceId],
    queryFn: () => workspaceFlowsApi.getReview(workspaceId || ''),
    enabled: Boolean(workspaceId),
  })

  const saveQuestionnaireMutation = useMutation({
    mutationFn: (answers: QuestionnaireAnswers) => workspaceFlowsApi.saveQuestionnaire(workspaceId || '', answers),
    onSuccess: async (result) => {
      setQuestionnaireDraftAnswers(result.answers || {})
      setQuestionnaireVisibleQuestions(result.visible || [])
      queryClient.setQueryData(['workspace-questionnaire', workspaceId], result)
      await queryClient.refetchQueries({ queryKey: ['workspace-checklist', workspaceId], type: 'active' })
      await queryClient.invalidateQueries({ queryKey: ['workspace'] })
      if (questionnaireCacheKey) {
        localStorage.setItem(questionnaireCacheKey, JSON.stringify({
          answers: result.answers || {},
          visible: result.visible || [],
          status: result.status || null,
        }))
      }
    },
  })

  const addIncomeMutation = useMutation({
    mutationFn: (payload: { type: 'paye' | 'interest' | 'dividends' | 'other'; body: Record<string, unknown> }) =>
      workspaceFlowsApi.addIncome(workspaceId || '', payload.type, payload.body),
    onSuccess: () => {
      setGross('')
      setPayeWithheld('')
      setIncomeAmount('')
      setIncomeSourceName('')

      // Keep the add-income button responsive by refreshing dependent queries in the background.
      void Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: ['workspace-income', workspaceId] }),
        queryClient.invalidateQueries({ queryKey: ['workspace-calc', workspaceId] }),
        queryClient.invalidateQueries({ queryKey: ['workspace-export', workspaceId] }),
        queryClient.invalidateQueries({ queryKey: ['workspace-review', workspaceId] }),
        queryClient.invalidateQueries({ queryKey: ['workspaces'] }),
        queryClient.invalidateQueries({ queryKey: ['workspace'] }),
      ])
    },
  })

  const importCryptoMutation = useMutation({
    mutationFn: (csv: string) => workspaceFlowsApi.importCryptoCsv(workspaceId || '', csv),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['workspace-crypto', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspace-calc', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspace-export', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspace-review', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      await queryClient.invalidateQueries({ queryKey: ['workspace'] })
    },
  })

  const saveAdjustmentsMutation = useMutation({
    mutationFn: (adjustments: WorkspaceAdjustments) => workspaceFlowsApi.saveAdjustments(workspaceId || '', adjustments),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['workspace-adjustments', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspace-calc', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspace-export', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspace-review', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      await queryClient.invalidateQueries({ queryKey: ['workspace'] })
    },
  })

  const uploadDocumentMutation = useMutation({
    mutationFn: (payload: { file: File; docType: string; donationAmount?: number }) => workspaceFlowsApi.uploadDocument(workspaceId || '', payload.file, payload.docType, payload.donationAmount),
    onSuccess: () => {
      setDocFile(null)
      setDonationReceiptAmount('0')

      // Avoid keeping the upload button in a pending state while dependent queries refresh.
      void Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: ['workspace-docs', workspaceId] }),
        queryClient.invalidateQueries({ queryKey: ['workspace-checklist', workspaceId] }),
        queryClient.invalidateQueries({ queryKey: ['workspace-export', workspaceId] }),
        queryClient.invalidateQueries({ queryKey: ['workspace-review', workspaceId] }),
        queryClient.invalidateQueries({ queryKey: ['workspaces'] }),
        queryClient.invalidateQueries({ queryKey: ['workspace'] }),
      ])
    },
  })

  const updateDocumentDonationAmountMutation = useMutation({
    mutationFn: (payload: { documentId: string; donationAmount: number }) =>
      workspaceFlowsApi.updateDocumentDonationAmount(workspaceId || '', payload.documentId, payload.donationAmount),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['workspace-docs', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspace-calc', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspace-export', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspace-review', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      await queryClient.invalidateQueries({ queryKey: ['workspace'] })
    },
  })

  const updateDocumentTypeMutation = useMutation({
    mutationFn: (payload: { documentId: string; docType: string }) =>
      workspaceFlowsApi.updateDocumentType(workspaceId || '', payload.documentId, payload.docType),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['workspace-docs', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspace-checklist', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspace-export', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspace-review', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      await queryClient.invalidateQueries({ queryKey: ['workspace'] })
    },
  })

  const updateDocumentEvidenceLinkMutation = useMutation({
    mutationFn: (payload: { documentId: string; evidenceLinks: DocumentEvidenceLinksPayload }) =>
      workspaceFlowsApi.updateDocumentEvidenceLink(workspaceId || '', payload.documentId, payload.evidenceLinks),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['workspace-docs', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspace-export', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspace-review', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      await queryClient.invalidateQueries({ queryKey: ['workspace'] })
    },
  })

  const deleteDocumentMutation = useMutation({
    mutationFn: (documentId: string) =>
      workspaceFlowsApi.deleteDocument(workspaceId || '', documentId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['workspace-docs', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspace-export', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspace-review', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      await queryClient.invalidateQueries({ queryKey: ['workspace'] })
    },
  })

  const saveReviewerActionStatusMutation = useMutation({
    mutationFn: (payload: { actionId: string; status: 'open' | 'resolved'; title?: string; note?: string }) =>
      workspaceFlowsApi.saveReviewerActionStatus(workspaceId || '', payload.actionId, {
        status: payload.status,
        title: payload.title,
        note: payload.note,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['workspace-export', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspace-review', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspace-audit', workspaceId, auditCategory, auditSearch] })
      await queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      await queryClient.invalidateQueries({ queryKey: ['workspace'] })
    },
  })

  const saveReviewerFinalSignoffMutation = useMutation({
    mutationFn: (payload: { overrideReason?: string }) =>
      workspaceFlowsApi.saveReviewerFinalSignoff(workspaceId || '', payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['workspace-export', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspace-review', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspace-audit', workspaceId, auditCategory, auditSearch] })
      await queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      await queryClient.invalidateQueries({ queryKey: ['workspace'] })
    },
  })

  const saveOperatorHandoffAckMutation = useMutation({
    mutationFn: (payload: { note?: string }) =>
      workspaceFlowsApi.saveOperatorHandoffAck(workspaceId || '', payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['workspace-export', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspace-review', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspace-audit', workspaceId, auditCategory, auditSearch] })
      await queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      await queryClient.invalidateQueries({ queryKey: ['workspace'] })
    },
  })

  const saveFilingCheckpointMutation = useMutation({
    mutationFn: (payload: { status: 'not_started' | 'submitted' | 'confirmed'; irdReference?: string; note?: string; submittedAt?: string; confirmedAt?: string }) =>
      workspaceFlowsApi.saveFilingCheckpoint(workspaceId || '', payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['workspace-export', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspace-review', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspace-audit', workspaceId, auditCategory, auditSearch] })
      await queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      await queryClient.invalidateQueries({ queryKey: ['workspace'] })
    },
  })

  const saveWarningEvidenceOverrideMutation = useMutation({
    mutationFn: (payload: { warningCode: string; mode: 'auto' | 'manual' | 'none'; documentIds?: string[] }) =>
      workspaceFlowsApi.saveWarningEvidenceOverride(workspaceId || '', payload.warningCode, {
        mode: payload.mode,
        documentIds: payload.documentIds,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['workspace-export', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspace-review', workspaceId] })
      await queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      await queryClient.invalidateQueries({ queryKey: ['workspace'] })
    },
  })

  const answers = useMemo(() => {
    const nextAnswers = {
      ...questionnaireDraftAnswers,
    }
    if (!isAnsweredQuestionValue(nextAnswers.questionnaire_mode)) {
      nextAnswers.questionnaire_mode = 'minimal'
    }
    if (!isAnsweredQuestionValue(nextAnswers.client_email) && userEmail) {
      nextAnswers.client_email = userEmail
    }
    if (!isAnsweredQuestionValue(nextAnswers.balance_date) && defaultBalanceDate) {
      nextAnswers.balance_date = defaultBalanceDate
    }
    return nextAnswers
  }, [defaultBalanceDate, questionnaireDraftAnswers, userEmail])
  const visibleQuestions = useMemo(() => {
    const schemaQuestions = questionnaireQuery.data?.schema || []
    if (schemaQuestions.length > 0) {
      return schemaQuestions.filter((question) => {
        if (!question.visibleIf) return true
        return Object.entries(question.visibleIf).every(([key, expected]) => answers[key] === expected)
      })
    }

    return questionnaireVisibleQuestions.length > 0
      ? questionnaireVisibleQuestions
      : (questionnaireQuery.data?.visible || [])
  }, [answers, questionnaireQuery.data?.schema, questionnaireQuery.data?.visible, questionnaireVisibleQuestions])

  const status = questionnaireQuery.data?.status
  const visibleQuestionIds = useMemo(() => new Set(visibleQuestions.map((question) => question.id)), [visibleQuestions])
  const questionById = useMemo(() => new Map(visibleQuestions.map((question) => [question.id, question])), [visibleQuestions])
  const questionParentMap = useMemo(() => {
    const parentMap = new Map<string, string>()
    visibleQuestions.forEach((question) => {
      const visibleIfEntries = Object.entries(question.visibleIf || {})
      if (visibleIfEntries.length !== 1) return
      const [parentId, expectedValue] = visibleIfEntries[0]
      const parentQuestion = questionById.get(parentId)
      if (!parentQuestion || !visibleQuestionIds.has(parentId) || expectedValue !== true || parentQuestion.type !== 'boolean') return
      parentMap.set(question.id, parentId)
    })
    return parentMap
  }, [questionById, visibleQuestionIds, visibleQuestions])
  const questionChildrenMap = useMemo(() => {
    const childMap = new Map<string, typeof visibleQuestions>()
    visibleQuestions.forEach((question) => {
      const parentId = questionParentMap.get(question.id)
      if (!parentId) return
      childMap.set(parentId, [...(childMap.get(parentId) || []), question])
    })
    return childMap
  }, [questionParentMap, visibleQuestions])
  const rootVisibleQuestions = useMemo(() => visibleQuestions.filter((question) => !questionParentMap.has(question.id)), [questionParentMap, visibleQuestions])
  const incomeBuckets: IncomeBucket | undefined = incomeQuery.data
  const payeRows = incomeBuckets?.paye || []
  const interestRows = incomeBuckets?.interest || []
  const dividendRows = incomeBuckets?.dividends || []
  const otherIncomeRows = incomeBuckets?.other || []
  const cryptoRows = cryptoQuery.data || []
  const docsResult = docsQuery.data || { items: [], evidenceLinkOptions: [] }
  const docs: WorkspaceDocument[] = docsResult.items || []
  const evidenceLinkOptions: EvidenceLinkOption[] = docsResult.evidenceLinkOptions || []
  const checklist = useMemo(() => checklistQuery.data || [], [checklistQuery.data])
  const audit: AuditResult = auditQuery.data || {
    events: [],
    summary: { totalEvents: 0, latestEventAt: null, byCategory: {} },
    overallSummary: { totalEvents: 0, latestEventAt: null, byCategory: {} },
    availableCategories: [],
    filters: { category: null, q: null, limit: null },
  }

  const saveFilingCheckpoint = (status: 'not_started' | 'submitted' | 'confirmed', currentReference?: string) => {
    if (status === 'not_started') {
      if (!window.confirm('Reset the filing checkpoint back to not started?')) return
      saveFilingCheckpointMutation.mutate({ status })
      return
    }

    const label = filingCheckpointStatusLabel(status)
    const irdReference = window.prompt(`Optional IRD reference for ${label.toLowerCase()}:`, currentReference || '') || ''
    const note = window.prompt(`Optional note for ${label.toLowerCase()}:`, '') || ''
    saveFilingCheckpointMutation.mutate({
      status,
      irdReference: irdReference.trim() || undefined,
      note: note.trim() || undefined,
    })
  }

  const summaryItems = useMemo(() => {
    const mapped = calcQuery.data?.map || {}
    const calc = calcQuery.data?.calc || {}
    return [...Object.entries(mapped), ...Object.entries(calc)].filter(([key]) => key !== 'summary')
  }, [calcQuery.data])

  const explanation = exportQuery.data?.explanation || calcQuery.data?.explanation
  const fieldNotesByRef = useMemo(() => {
    const notes = explanation?.fieldNotes || []
    return Object.fromEntries(notes.map((note: { ref: string; label?: string; note?: string; source?: string }) => [note.ref, note]))
  }, [explanation])
  const review: ReviewPayload | undefined = reviewQuery.data || exportQuery.data?.review
  const reviewWarnings = useMemo(() => review?.warnings || [], [review?.warnings])
  const reviewEvidence = useMemo(() => review?.evidence || [], [review?.evidence])
  const submissionReadiness = review?.submissionReadiness
  const reviewerActionQueue = review?.reviewerActionQueue
  const clientFullName = typeof answers.client_full_name === 'string' ? answers.client_full_name.trim() : ''
  const reviewEvidenceByIr3Ref = useMemo(() => {
    return reviewEvidence.reduce((acc, item) => {
      for (const ref of item.ir3Refs || []) {
        acc[ref] = [...(acc[ref] || []), item]
      }
      return acc
    }, {} as Record<string, ReviewEvidenceItem[]>)
  }, [reviewEvidence])
  const highSeverityWarningCount = reviewWarnings.filter((warning) => warning.severity === 'high').length
  const finalReviewTabIndex = 5
  const finalReviewAttentionCount = Math.max(reviewerActionQueue?.totalCount || 0, submissionReadiness?.blockerCount || 0, reviewWarnings.length)
  const receivedChecklistItems = useMemo(() => checklist.filter((item) => item.status === 'received'), [checklist])
  const nextStepItems = useMemo(() => {
    return [
      {
        key: 'questions',
        label: 'Start with your details',
        detail: 'Work through the questions section first so the rest of the workspace has the right context.',
        actionLabel: 'Open questions',
        action: () => setTab(0),
      },
      {
        key: 'income',
        label: 'Add the income figures',
        detail: 'Enter PAYE, interest, dividends, crypto, or other income as you gather the records.',
        actionLabel: 'Open income',
        action: () => setTab(1),
      },
      {
        key: 'documents',
        label: 'Upload supporting records',
        detail: 'Attach documents as they arrive so the pack is easier to hand over later.',
        actionLabel: 'Open documents',
        action: () => setTab(3),
      },
      {
        key: 'ir3',
        label: 'Review the trial IR3',
        detail: 'Use the draft summary to sense-check the figures before handoff.',
        actionLabel: 'Open trial IR3',
        action: () => setTab(5),
      },
      {
        key: 'handoff',
        label: 'Use Accountant handoff for gaps',
        detail: 'That is the one place where the app should summarise what is still missing or needs review.',
        actionLabel: 'Open handoff',
        action: () => setTab(finalReviewTabIndex),
      },
    ]
  }, [finalReviewTabIndex])

  const adjustments = adjustmentsQuery.data || {
    donationAmount: 0,
    pieIncome: 0,
    pieTaxCredits: 0,
    extraTaxDeducted: 0,
    studentLoanRepayments: 0,
  }

  useEffect(() => {
    setDonationAmount(String(adjustments.donationAmount ?? 0))
    setPieIncome(String(adjustments.pieIncome ?? 0))
    setPieTaxCredits(String(adjustments.pieTaxCredits ?? 0))
    setExtraTaxDeducted(String(adjustments.extraTaxDeducted ?? 0))
    setStudentLoanRepayments(String(adjustments.studentLoanRepayments ?? 0))
  }, [adjustments.donationAmount, adjustments.pieIncome, adjustments.pieTaxCredits, adjustments.extraTaxDeducted, adjustments.studentLoanRepayments])

  useEffect(() => {
    if (!questionnaireCacheKey) return
    try {
      const raw = localStorage.getItem(questionnaireCacheKey)
      if (!raw) return
      const cached = JSON.parse(raw) as { answers?: QuestionnaireAnswers; visible?: QuestionnaireQuestion[] }
      if (cached.answers && Object.keys(cached.answers).length > 0) {
        setQuestionnaireDraftAnswers(cached.answers)
      }
      if (Array.isArray(cached.visible) && cached.visible.length > 0) {
        setQuestionnaireVisibleQuestions(cached.visible)
      }
    } catch {
      // Ignore invalid local cache.
    }
  }, [questionnaireCacheKey])

  useEffect(() => {
    if (!questionnaireQuery.data) return
    setQuestionnaireDraftAnswers(questionnaireQuery.data.answers || {})
    setQuestionnaireVisibleQuestions(questionnaireQuery.data.visible || [])
    setQuestionTextDrafts({})
    if (questionnaireCacheKey) {
      localStorage.setItem(questionnaireCacheKey, JSON.stringify(questionnaireQuery.data))
    }
  }, [questionnaireCacheKey, questionnaireQuery.data, questionnaireQuery.dataUpdatedAt])

  useEffect(() => {
    if (!workspaceId || !questionnaireQuery.data) return

    const nextPrefills: QuestionnaireAnswers = {}
    if (!isAnsweredQuestionValue(questionnaireDraftAnswers.questionnaire_mode)) {
      nextPrefills.questionnaire_mode = 'minimal'
    }
    if (!isAnsweredQuestionValue(questionnaireDraftAnswers.client_email) && userEmail) {
      nextPrefills.client_email = userEmail
    }
    if (!isAnsweredQuestionValue(questionnaireDraftAnswers.balance_date) && defaultBalanceDate) {
      nextPrefills.balance_date = defaultBalanceDate
    }
    if (Object.keys(nextPrefills).length === 0) return

    const nextAnswers = {
      ...questionnaireDraftAnswers,
      ...nextPrefills,
    }

    setQuestionnaireDraftAnswers(nextAnswers)
    saveQuestionnaireMutation.mutate(nextAnswers, { onSuccess: () => setLastSavedAt(new Date().toLocaleTimeString()) })
  }, [defaultBalanceDate, questionnaireDraftAnswers, questionnaireQuery.data, saveQuestionnaireMutation, userEmail, workspaceId])

  const downloadTextFile = (filename: string, content: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    anchor.click()
    URL.revokeObjectURL(url)
  }

  const downloadBinaryFile = (filename: string, bytes: ArrayBuffer, mimeType: string) => {
    const blob = new Blob([bytes], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    anchor.click()
    URL.revokeObjectURL(url)
  }

  const downloadPdfFile = () => {
    const pdf = exportQuery.data?.pdf
    if (!pdf?.bytesBase64) return
    const binary = atob(pdf.bytesBase64)
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0))
    const blob = new Blob([bytes], { type: pdf.mimeType || 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = pdf.filename || 'ir3-draft.pdf'
    anchor.click()
    URL.revokeObjectURL(url)
  }

  const downloadJsonFile = () => {
    if (!exportQuery.data?.json) return
    downloadTextFile(
      `ir3-draft-${workspaceId || 'workspace'}.json`,
      `${JSON.stringify(exportQuery.data.json, null, 2)}\n`,
      'application/json;charset=utf-8',
    )
  }

  const copyCsvToClipboard = async () => {
    const csv = exportQuery.data?.csv
    if (!csv) return
    try {
      await navigator.clipboard.writeText(csv)
      setCopyFeedback('CSV copied to clipboard.')
    } catch {
      setCopyFeedback('Clipboard copy failed on this browser.')
    }
  }

  const downloadAccountantPack = async () => {
    if (!workspaceId) return
    setAccountantPackFeedback(null)
    setIsDownloadingAccountantPack(true)
    try {
      const pack = await workspaceFlowsApi.downloadAccountantPack(workspaceId)
      downloadBinaryFile(pack.filename, pack.bytes, pack.mimeType)
      setAccountantPackFeedback('Accountant zip downloaded.')
    } catch {
      setAccountantPackFeedback('Could not download the accountant zip from the server.')
    } finally {
      setIsDownloadingAccountantPack(false)
    }
  }

  const openAccountantEmailDraft = () => {
    const subjectName = clientFullName || `workspace ${workspaceId || ''}`
    const bodyLines = [
      'Hi,',
      '',
      `Please find my NZ tax handoff pack for ${subjectName}.`,
      '',
      'I have attached the downloaded accountant zip from the app.',
      '',
      'Thanks,',
      clientFullName || '',
    ].filter(Boolean)

    window.location.href = `mailto:?subject=${encodeURIComponent(`NZ tax handoff pack - ${subjectName}`)}&body=${encodeURIComponent(bodyLines.join('\n'))}`
    setAccountantPackFeedback('Email draft opened. Attach the downloaded accountant zip before sending.')
  }

  const handleSaveAdjustments = () => {
    saveAdjustmentsMutation.mutate({
      donationAmount: Number(donationAmount || 0),
      pieIncome: Number(pieIncome || 0),
      pieTaxCredits: Number(pieTaxCredits || 0),
      extraTaxDeducted: Number(extraTaxDeducted || 0),
      studentLoanRepayments: Number(studentLoanRepayments || 0),
    })
  }

  const saveQuestionnaireAnswer = (questionId: string, value: QuestionnaireAnswers[string]) => {
    const nextAnswers = {
      ...answers,
      [questionId]: value,
    }
    setQuestionnaireDraftAnswers(nextAnswers)
    saveQuestionnaireMutation.mutate(nextAnswers, { onSuccess: () => setLastSavedAt(new Date().toLocaleTimeString()) })
  }

  const getQuestionTextValue = (questionId: string) => {
    if (questionTextDrafts[questionId] !== undefined) return questionTextDrafts[questionId]
    const value = answers[questionId]
    return typeof value === 'string' ? value : ''
  }

  const dashboard = useMemo(() => {
    const questionnaireDone = status?.answeredVisible || 0
    const questionnaireTotal = status?.totalVisible || 0
    const incomeCount = payeRows.length + interestRows.length + dividendRows.length + otherIncomeRows.length
    const incomeDone = incomeCount > 0 ? 1 : 0
    const cryptoDone = cryptoRows.length > 0 ? 1 : 0
    const calcDone = summaryItems.length > 0 ? 1 : 0
    const sections = [
      { label: 'Your information', done: questionnaireDone, total: questionnaireTotal || 1, detail: questionnaireTotal ? `${questionnaireDone}/${questionnaireTotal} answered` : 'No visible questions yet' },
      { label: 'Income', done: incomeDone, total: 1, detail: incomeCount > 0 ? `${incomeCount} income entries added` : 'No income entries yet' },
      { label: 'Crypto', done: cryptoDone, total: 1, detail: cryptoRows.length > 0 ? `${cryptoRows.length} transactions imported` : 'No crypto data yet' },
      { label: 'Trial IR3 Summary', done: calcDone, total: 1, detail: summaryItems.length > 0 ? `${summaryItems.length} values generated` : 'No calculation output yet' },
    ]
    const doneUnits = sections.reduce((sum, section) => sum + Math.min(section.done, section.total), 0)
    const totalUnits = sections.reduce((sum, section) => sum + section.total, 0)
    return {
      sections,
      overallPercent: percent(doneUnits, totalUnits),
    }
  }, [status, payeRows.length, interestRows.length, dividendRows.length, otherIncomeRows.length, cryptoRows.length, summaryItems.length])

  if (!isLoading && !isAuthenticated) {
    return <Navigate to="/" replace />
  }

  const renderQuestionField = (question: typeof visibleQuestions[number], isUnanswered: boolean, isActionStep: boolean) => {
    if (question.uiMode === 'documents_link') {
      return (
        <Stack spacing={1}>
          <Typography variant="subtitle1">{question.label}</Typography>
          <Typography variant="body2" color="text.secondary">
            {question.helperText || 'Open Documents to upload the supporting files for this step.'}
          </Typography>
          <Button variant="outlined" onClick={() => setTab(3)} sx={{ alignSelf: 'flex-start' }}>
            Open documents upload
          </Button>
        </Stack>
      )
    }

    if (question.type === 'boolean') {
      return (
        <Stack spacing={1}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
            <Box>
              <Typography variant="subtitle1">{question.label}</Typography>
              <Typography variant="body2" color="text.secondary">
                {question.helperText || 'Answer with your current best understanding and update it when records arrive.'}
              </Typography>
            </Box>
            {isActionStep ? <Chip label="Next action" color="info" variant="outlined" sx={{ alignSelf: 'flex-start' }} /> : null}
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant={answers[question.id] === true ? 'contained' : 'outlined'}
              color={isActionStep ? 'success' : 'primary'}
              onClick={() => saveQuestionnaireAnswer(question.id, true)}
              sx={{ minWidth: { sm: 120 } }}
            >
              {isActionStep ? 'Done' : 'Yes'}
            </Button>
            <Button
              variant={answers[question.id] === false ? 'contained' : 'outlined'}
              color={answers[question.id] === false ? 'inherit' : 'primary'}
              onClick={() => saveQuestionnaireAnswer(question.id, false)}
              sx={{ minWidth: { sm: 120 } }}
            >
              {isActionStep ? 'Later' : 'No'}
            </Button>
            {isActionStep ? (
              <Button variant="text" onClick={() => setTab(3)}>
                Open documents
              </Button>
            ) : null}
          </Stack>
        </Stack>
      )
    }

    if (question.type === 'select') {
      return (
        <TextField
          select
          label={question.label}
          helperText={question.helperText || 'Choose the closest option.'}
          error={isUnanswered}
          value={typeof answers[question.id] === 'string' ? String(answers[question.id]) : ''}
          onChange={(event) => saveQuestionnaireAnswer(question.id, event.target.value)}
          fullWidth
        >
          <MenuItem value="">Choose…</MenuItem>
          {(question.options || []).map((option) => (
            <MenuItem key={`${question.id}-${option.value}`} value={option.value}>{option.label}</MenuItem>
          ))}
        </TextField>
      )
    }

    if (question.type === 'multiselect') {
      return (
        <TextField
          select
          label={question.label}
          helperText={question.helperText || 'Select all that apply.'}
          error={isUnanswered}
          value={Array.isArray(answers[question.id]) ? answers[question.id] : []}
          onChange={(event) => {
            const value = event.target.value
            saveQuestionnaireAnswer(question.id, Array.isArray(value) ? value : [String(value)])
          }}
          fullWidth
          SelectProps={{
            multiple: true,
            renderValue: (selected) => Array.isArray(selected) ? selected.map((item) => {
              const option = (question.options || []).find((candidate) => candidate.value === item)
              return option?.label || item
            }).join(', ') : '',
          }}
        >
          {(question.options || []).map((option) => (
            <MenuItem key={`${question.id}-${option.value}`} value={option.value}>{option.label}</MenuItem>
          ))}
        </TextField>
      )
    }

    if (question.type === 'text' || question.type === 'multiline') {
      return (
        <TextField
          label={question.label}
          helperText={question.helperText || 'Saved when you leave the field.'}
          placeholder={question.placeholder}
          error={isUnanswered}
          value={getQuestionTextValue(question.id)}
          multiline={question.type === 'multiline'}
          minRows={question.type === 'multiline' ? 4 : undefined}
          onChange={(event) => {
            const value = event.target.value
            setQuestionTextDrafts((current) => ({
              ...current,
              [question.id]: value,
            }))
          }}
          onBlur={(event) => {
            const value = event.target.value
            if (value === (typeof answers[question.id] === 'string' ? answers[question.id] : '')) return
            saveQuestionnaireAnswer(question.id, value)
          }}
          fullWidth
        />
      )
    }

    return null
  }

  const renderQuestionTree = (question: typeof visibleQuestions[number], depth = 0) => {
    const isActionStep = question.uiMode === 'action' || question.uiMode === 'documents_link'
    const isUnanswered = !isActionStep && !isAnsweredQuestionValue(answers[question.id])
    const childQuestions = questionChildrenMap.get(question.id) || []

    return (
      <Stack
        key={question.id}
        id={`question-${question.id}`}
        spacing={1.25}
        sx={{
          border: 1,
          borderColor: isUnanswered ? 'warning.light' : isActionStep ? 'info.light' : 'divider',
          borderRadius: 2,
          p: { xs: 1.5, md: 2 },
          pl: depth > 0 ? { xs: 2, md: 2.5 } : undefined,
          ml: depth > 0 ? { xs: 1, md: 1.5 } : undefined,
          bgcolor: isUnanswered ? 'warning.50' : isActionStep ? alpha('#0284c7', 0.04) : depth > 0 ? alpha('#0f172a', 0.015) : 'transparent',
          borderLeftWidth: depth > 0 ? 4 : 1,
        }}
      >
        {renderQuestionField(question, isUnanswered, isActionStep)}
        {question.id === 'has_working_for_families_or_parental_tax_credit' && answers[question.id] === true ? (
          <Alert severity="info" sx={{ mt: 0.5 }}>
            Only fill in the family items that actually apply. Start with child and partner details, then open the smaller checks only if something changed.
          </Alert>
        ) : null}
        {childQuestions.length > 0 && answers[question.id] === true ? (
          question.id === 'has_working_for_families_or_parental_tax_credit' ? (
            <Stack spacing={1.5} sx={{ pt: 0.5 }}>
              {familyBranchGroups.map((group) => {
                const groupQuestions = childQuestions.filter((child) => group.questionIds.includes(child.id as never))
                if (groupQuestions.length === 0) return null
                return (
                  <Stack key={group.key} spacing={1} sx={{ borderTop: 1, borderColor: 'divider', pt: 1.5 }}>
                    <Box>
                      <Typography variant="overline" color="text.secondary">{group.title}</Typography>
                      <Typography variant="body2" color="text.secondary">{group.detail}</Typography>
                    </Box>
                    <Stack spacing={1}>
                      {groupQuestions.map((child) => renderQuestionTree(child, depth + 1))}
                    </Stack>
                  </Stack>
                )
              })}
              {childQuestions.filter((child) => !familyBranchGroups.some((group) => group.questionIds.includes(child.id as never))).length > 0 ? (
                <Stack spacing={1} sx={{ borderTop: 1, borderColor: 'divider', pt: 1.5 }}>
                  <Typography variant="overline" color="text.secondary">Other family details</Typography>
                  <Stack spacing={1}>
                    {childQuestions
                      .filter((child) => !familyBranchGroups.some((group) => group.questionIds.includes(child.id as never)))
                      .map((child) => renderQuestionTree(child, depth + 1))}
                  </Stack>
                </Stack>
              ) : null}
            </Stack>
          ) : (
            <Stack spacing={1} sx={{ pt: 0.5 }}>
              {childQuestions.map((child) => renderQuestionTree(child, depth + 1))}
            </Stack>
          )
        ) : null}
      </Stack>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: [
          'radial-gradient(circle at 10% 12%, rgba(8,145,178,0.10), transparent 22%)',
          'radial-gradient(circle at 88% 14%, rgba(5,150,105,0.08), transparent 18%)',
          'linear-gradient(180deg, #f9fbfc 0%, #f3f7f8 42%, #eef2f1 100%)',
        ].join(','),
      }}
    >
    <Container maxWidth="xl">
      <Stack spacing={{ xs: 3, md: 4 }} sx={{ py: { xs: 3, sm: 4, md: 6 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '2rem', sm: '2.4rem', md: '2.8rem' }, maxWidth: 700 }}>
              TaxHandoff NZ workspace
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontFamily: bodyFont, maxWidth: 760 }}>
              Gather the answers, records, and explanations your accountant needs, then review a trial IR3 summary before handoff.
            </Typography>
          </Box>
          <Button component={RouterLink} to="/workspaces" variant="outlined" sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}>
            Back to workspaces
          </Button>
        </Stack>

        {workspaceQuery.data ? (
          <Card sx={{ bgcolor: alpha('#fffdf8', 0.9) }}>
            <CardContent>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.25}>
                  <Typography variant="h5">
                    {workspaceQuery.data.name
                      ? `Workspace ${workspaceQuery.data.name}`
                      : `Accountant workspace ${workspaceQuery.data.id.slice(0, 8)}`}
                  </Typography>
                  <Chip
                    label={workspaceQuery.data.status || 'unknown'}
                    color={workspaceQuery.data.status === 'in_progress' ? 'primary' : 'default'}
                    variant="outlined"
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                  Tax year covered: {workspaceQuery.data.taxYearStart} to {workspaceQuery.data.taxYearEnd}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                  Use this space to collect the answers, figures, and documents that belong to this one NZ tax year only.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                  Updated: {formatDate(workspaceQuery.data.updatedAt)}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        ) : null}

        <Card sx={{ bgcolor: alpha('#ffffff', 0.74) }}>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">What to do next</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                Keep the next step simple. You do not need every section if it does not apply to your situation.
              </Typography>
              <Box>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                    Overall progress
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                    {dashboard.overallPercent}%
                  </Typography>
                </Stack>
                <LinearProgress variant="determinate" value={dashboard.overallPercent} sx={{ height: 10, borderRadius: 5 }} />
              </Box>
              <Stack spacing={1.5}>
                {nextStepItems.map((item) => (
                  <Alert
                    key={item.key}
                    severity={item.key === 'handoff' ? 'success' : 'info'}
                    action={item.actionLabel && item.action ? (
                      <Button color="inherit" size="small" onClick={item.action}>
                        {item.actionLabel}
                      </Button>
                    ) : undefined}
                  >
                    <Typography variant="body2"><strong>{item.label}.</strong> {item.detail}</Typography>
                  </Alert>
                ))}
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: alpha('#ffffff', 0.74) }}>
          <CardContent>
            <Tabs value={tab} onChange={(_e, value) => setTab(value)} variant="scrollable" scrollButtons="auto" sx={{ '& .MuiTab-root': { fontSize: { xs: '0.9rem', md: '0.95rem' } } }}>
              <Tab label="About you" />
              <Tab label="Income" />
              <Tab label="Crypto" />
              <Tab label="Docs" />
              <Tab label="Trial IR3" />
              <Tab label={finalReviewAttentionCount > 0 ? `Handoff (${finalReviewAttentionCount})` : 'Handoff'} />
              <Tab label="History" />
            </Tabs>

            <Divider sx={{ my: 2 }} />

            {tab === 0 ? (
              <Stack spacing={2}>
                <Typography variant="h6">Your information</Typography>
                <Typography variant="body2" color="text.secondary">
                  Work through these questions in plain English. You do not need perfect tax knowledge here. If you are unsure, write what you know, add a note, and keep going.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Quick translations: balance date means the year-end date for these records, PIE income usually means income shown on a KiwiSaver or managed-fund tax statement, and RLWT means tax that may have been withheld when a residential property was sold.
                </Typography>
                {status || saveQuestionnaireMutation.isPending || lastSavedAt ? (
                  <Typography variant="caption" color="text.secondary">
                    {saveQuestionnaireMutation.isPending
                      ? 'Saving your answers…'
                      : lastSavedAt
                        ? `Answers saved at ${lastSavedAt}`
                        : status?.complete
                          ? 'Answers save automatically as you go.'
                          : 'Answers save automatically as you go.'}
                  </Typography>
                ) : null}
                  {!questionnaireQuery.isLoading && visibleQuestions.length === 0 ? (
                    <Alert
                      severity="warning"
                      action={
                        <Button color="inherit" size="small" onClick={() => questionnaireQuery.refetch()}>
                          Reload questions
                        </Button>
                      }
                    >
                      Questions did not load yet. Reload to fetch them again.
                    </Alert>
                  ) : null}
                {rootVisibleQuestions.map((question, index) => {
                  const showSectionHeader = question.section && question.section !== rootVisibleQuestions[index - 1]?.section

                  return (
                    <Stack key={question.id} spacing={1}>
                      {showSectionHeader ? (
                        <Box sx={{ pt: index === 0 ? 0 : 1 }}>
                          <Typography variant="overline" color="text.secondary">
                            {question.section}
                          </Typography>
                          {question.section ? (
                            <Typography variant="body2" color="text.secondary">
                              {sectionGuidance[question.section]}
                            </Typography>
                          ) : null}
                        </Box>
                      ) : null}
                      {renderQuestionTree(question)}
                    </Stack>
                  )
                })}
              </Stack>
            ) : null}

            {tab === 1 ? (
              <Stack spacing={2}>
                <Typography variant="h6">Income</Typography>
                <Alert severity="info">
                  Add income one source at a time. Use the gross amount before deductions unless the label says tax withheld. You can usually find these figures on payslips, bank interest summaries, dividend statements, or year-end tax certificates.
                </Alert>
                <TextField select label="Income type" value={incomeType} onChange={(event) => setIncomeType(event.target.value as 'paye' | 'interest' | 'dividends' | 'other')} helperText="Pick the kind of income you are adding so the app asks for the right figures." fullWidth>
                  <MenuItem value="paye">PAYE</MenuItem>
                  <MenuItem value="interest">Interest</MenuItem>
                  <MenuItem value="dividends">Dividends</MenuItem>
                  <MenuItem value="other">Other income</MenuItem>
                </TextField>
                {incomeType === 'paye' ? (
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField label="Gross income (NZD)" type="number" value={gross} onChange={(event) => setGross(event.target.value)} helperText="Enter total pay before tax and deductions. This is often called gross earnings on your payslip or earnings summary." fullWidth />
                    <TextField label="PAYE withheld (NZD)" type="number" value={payeWithheld} onChange={(event) => setPayeWithheld(event.target.value)} helperText="Enter the tax already taken out by your employer. Look for PAYE tax deducted or tax withheld." fullWidth />
                  </Stack>
                ) : (
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField label="Amount (NZD)" type="number" value={incomeAmount} onChange={(event) => setIncomeAmount(event.target.value)} helperText="Enter the amount shown on the statement or summary for this income source." fullWidth />
                    <TextField label="Source" value={incomeSourceName} onChange={(event) => setIncomeSourceName(event.target.value)} helperText="Example: ANZ savings account, Sharesies dividend, side-job invoice, or rental top-up." fullWidth />
                  </Stack>
                )}
                <Button
                  variant="contained"
                  disabled={incomeType === 'paye' ? (!gross || !payeWithheld || addIncomeMutation.isPending) : (!incomeAmount || addIncomeMutation.isPending)}
                  onClick={() => addIncomeMutation.mutate({
                    type: incomeType,
                    body: incomeType === 'paye'
                      ? { gross: Number(gross), payeWithheld: Number(payeWithheld) }
                      : { amount: Number(incomeAmount), sourceName: incomeSourceName },
                  })}
                >
                  {addIncomeMutation.isPending ? 'Saving…' : `Add ${incomeType} income`}
                </Button>
                {payeRows.length + interestRows.length + dividendRows.length + otherIncomeRows.length > 0 ? (
                  <Stack spacing={1}>
                    {payeRows.map((row) => (
                      <Card key={row.id} variant="outlined"><CardContent><Typography variant="body1">PAYE · Gross: ${row.gross ?? row.amount ?? 0}</Typography><Typography variant="body2" color="text.secondary">PAYE withheld: ${row.payeWithheld ?? row.taxWithheld ?? 0}</Typography></CardContent></Card>
                    ))}
                    {interestRows.map((row) => (
                      <Card key={row.id} variant="outlined"><CardContent><Typography variant="body1">Interest · ${row.amount ?? row.gross ?? 0}</Typography><Typography variant="body2" color="text.secondary">Source: {row.sourceName || row.payer || row.employer || '—'}</Typography></CardContent></Card>
                    ))}
                    {dividendRows.map((row) => (
                      <Card key={row.id} variant="outlined"><CardContent><Typography variant="body1">Dividends · ${row.amount ?? row.gross ?? 0}</Typography><Typography variant="body2" color="text.secondary">Source: {row.sourceName || row.payer || row.employer || '—'}</Typography></CardContent></Card>
                    ))}
                    {otherIncomeRows.map((row) => (
                      <Card key={row.id} variant="outlined"><CardContent><Typography variant="body1">Other income · ${row.amount ?? row.gross ?? 0}</Typography><Typography variant="body2" color="text.secondary">Source: {row.sourceName || row.payer || row.employer || '—'}</Typography></CardContent></Card>
                    ))}
                  </Stack>
                ) : (
                  <Alert severity="info">No income added yet.</Alert>
                )}
              </Stack>
            ) : null}

            {tab === 2 ? (
              <Stack spacing={2}>
                <Typography variant="h6">Crypto CSV import</Typography>
                <Alert severity="info">
                  Paste rows with these columns: date, asset, type, amount, price_nzd, fee_nzd, exchange. A sample row is prefilled so you can match the format before importing.
                </Alert>
                <TextField label="Paste CSV" value={csvText} onChange={(event) => setCsvText(event.target.value)} multiline minRows={6} fullWidth />
                <Button variant="contained" disabled={!csvText.trim() || importCryptoMutation.isPending} onClick={() => importCryptoMutation.mutate(csvText)}>
                  {importCryptoMutation.isPending ? 'Importing…' : 'Import crypto CSV'}
                </Button>
                {cryptoRows.length > 0 ? (
                  <Stack spacing={1}>
                    {cryptoRows.map((row) => (
                      <Card key={row.id} variant="outlined">
                        <CardContent>
                          <Typography variant="body1">{row.occurredAt} — {row.asset} {row.type} {row.amount}</Typography>
                          <Typography variant="body2" color="text.secondary">NZD price: ${row.priceNzd} · Fee: ${row.feeNzd} · Source: {row.source || '—'}</Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                ) : (
                  <Alert severity="info">No crypto transactions imported yet.</Alert>
                )}
              </Stack>
            ) : null}


            {tab === 3 ? (
              <Stack spacing={2}>
                <Typography variant="h6">Documents</Typography>
                <Alert severity="info">
                  Upload the records that support your figures and accountant notes. This makes it easier to hand over a complete pack later, especially if your accountant gave you a PDF checklist or extra requests.
                </Alert>
                <TextField
                  select
                  label="Document type"
                  value={docType}
                  onChange={(event) => setDocType(event.target.value)}
                  helperText="Choose the document type that best matches the file. Automatic evidence linking follows this type, and you can change it later if needed."
                  fullWidth
                >
                  <MenuItem value="">Choose…</MenuItem>
                  <MenuItem value="paye_summary">PAYE summary</MenuItem>
                  <MenuItem value="interest_dividend_slips">Interest/dividend slips</MenuItem>
                  <MenuItem value="student_loan_statement">Student loan statement</MenuItem>
                  <MenuItem value="crypto_csv">Crypto CSV</MenuItem>
                  <MenuItem value="donation_receipts">Donation receipts</MenuItem>
                  <MenuItem value="accountant_questionnaire">Accountant questionnaire / request</MenuItem>
                  <MenuItem value="working_for_families_certificate">Working for Families certificate</MenuItem>
                  <MenuItem value="property_sale_documents">Property sale documents</MenuItem>
                  <MenuItem value="overseas_investment_reports">Overseas investment or adviser reports</MenuItem>
                  <MenuItem value="home_office_records">Home office records</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </TextField>
                <Button variant="outlined" component="label">
                  {docFile ? `Selected: ${docFile.name}` : 'Choose file'}
                  <input
                    hidden
                    type="file"
                    onChange={(event) => setDocFile(event.target.files?.[0] || null)}
                  />
                </Button>
                {docType === 'donation_receipts' ? (
                  <TextField
                    label="Donation total (NZD)"
                    type="number"
                    value={donationReceiptAmount}
                    onChange={(e) => setDonationReceiptAmount(e.target.value)}
                    helperText="Optional for now, but adding the amount lets the draft total donation receipts automatically."
                    fullWidth
                  />
                ) : null}
                <Button
                  variant="contained"
                  disabled={!docFile || !docType || uploadDocumentMutation.isPending}
                  onClick={() => docFile && uploadDocumentMutation.mutate({ file: docFile, docType, donationAmount: docType === 'donation_receipts' ? Number(donationReceiptAmount || 0) : undefined })}
                >
                  {uploadDocumentMutation.isPending ? 'Uploading…' : 'Upload document'}
                </Button>

                <Typography variant="subtitle1">Checklist overview</Typography>
                {checklist.length > 0 ? (
                  <Stack spacing={1}>
                    {checklist.map((item) => (
                      <Card key={item.docType} variant="outlined">
                        <CardContent>
                          <Stack spacing={0.75}>
                            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                              <Typography variant="body1">{item.label}</Typography>
                              <Chip
                                size="small"
                                variant="outlined"
                                color={item.status === 'received' ? 'success' : 'warning'}
                                label={item.status === 'received' ? `Received (${item.count})` : 'Missing'}
                              />
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                              {item.reason}
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                ) : (
                  <Alert severity="success">No supporting documents are currently in scope for this draft.</Alert>
                )}

                {receivedChecklistItems.length > 0 ? (
                  <Stack spacing={1}>
                    <Typography variant="subtitle2">Already covered</Typography>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {receivedChecklistItems.map((item) => (
                        <Chip key={`document-covered-${item.docType}`} label={`${item.label} (${item.count})`} color="success" variant="outlined" />
                      ))}
                    </Stack>
                  </Stack>
                ) : null}

                <Typography variant="subtitle1">Uploaded documents</Typography>
                {docs.length > 0 ? (
                  <Stack spacing={1}>
                    {docs.map((doc) => {
                      const linkedEvidence = findEvidenceForDocument(doc.id, reviewEvidence)

                      return (
                        <Card key={doc.id} variant="outlined">
                          <CardContent>
                            <Stack spacing={1.25}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                                <Box>
                                  <Typography variant="body1">{doc.originalName}</Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {doc.docType} · {Math.round((doc.size || 0) / 1024)} KB · {doc.status}
                                  </Typography>
                                </Box>
                                <Button
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  disabled={deleteDocumentMutation.isPending}
                                  onClick={() => {
                                    if (window.confirm(`Delete "${doc.originalName}"?`)) {
                                      deleteDocumentMutation.mutate(doc.id)
                                    }
                                  }}
                                >
                                  Delete
                                </Button>
                              </Box>
                              <TextField
                                select
                                size="small"
                                label="Document type"
                                value={doc.docType}
                                onChange={(event) => {
                                  updateDocumentTypeMutation.mutate({
                                    documentId: doc.id,
                                    docType: event.target.value,
                                  })
                                }}
                                disabled={updateDocumentTypeMutation.isPending}
                                helperText="Automatic evidence linking follows this document type."
                                fullWidth
                              >
                                <MenuItem value="paye_summary">PAYE summary</MenuItem>
                                <MenuItem value="interest_dividend_slips">Interest/dividend slips</MenuItem>
                                <MenuItem value="student_loan_statement">Student loan statement</MenuItem>
                                <MenuItem value="crypto_csv">Crypto CSV</MenuItem>
                                <MenuItem value="donation_receipts">Donation receipts</MenuItem>
                                <MenuItem value="accountant_questionnaire">Accountant questionnaire / request</MenuItem>
                                <MenuItem value="working_for_families_certificate">Working for Families certificate</MenuItem>
                                <MenuItem value="property_sale_documents">Property sale documents</MenuItem>
                                <MenuItem value="overseas_investment_reports">Overseas investment or adviser reports</MenuItem>
                                <MenuItem value="home_office_records">Home office records</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                              </TextField>
                              {doc.docType === 'donation_receipts' ? (
                                <TextField
                                  size="small"
                                  label="Donation total (NZD)"
                                  type="number"
                                  value={String(doc.donationAmount ?? 0)}
                                  onChange={(event) => {
                                    updateDocumentDonationAmountMutation.mutate({
                                      documentId: doc.id,
                                      donationAmount: Number(event.target.value || 0),
                                    })
                                  }}
                                  disabled={updateDocumentDonationAmountMutation.isPending}
                                  helperText="Used in the donation receipts total that feeds the draft calculation."
                                  fullWidth
                                />
                              ) : null}
                              {linkedEvidence.length > 0 ? (
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                  {linkedEvidence.map((item) => (
                                    <Chip
                                      key={`${doc.id}-${item.supports}-${item.section}`}
                                      size="small"
                                      color={item.linkMode === 'manual' ? 'primary' : 'success'}
                                      variant="outlined"
                                      label={`${item.linkMode === 'manual' ? 'Manually linked' : 'Supports'} ${item.supports}`}
                                    />
                                  ))}
                                </Stack>
                              ) : (
                                <Typography variant="body2" color="text.secondary">Not yet linked to a review evidence area.</Typography>
                              )}
                              <TextField
                                select
                                size="small"
                                label="Evidence link"
                                value={getSelectedEvidenceLinkValues(doc, evidenceLinkOptions)}
                                onChange={(event) => {
                                  const selectedValues = normalizeEvidenceLinkSelection(event.target.value as unknown as string[])
                                  updateDocumentEvidenceLinkMutation.mutate({
                                    documentId: doc.id,
                                    evidenceLinks: resolveEvidenceLinkPayload(selectedValues, evidenceLinkOptions),
                                  })
                                }}
                                disabled={updateDocumentEvidenceLinkMutation.isPending}
                                helperText={`Default mapping preview: ${describeAutomaticEvidenceMapping(doc.docType)}. Choose a manual link only when you need to override this.`}
                                fullWidth
                                SelectProps={{
                                  multiple: true,
                                  renderValue: (selected) => describeEvidenceLinkSelection(selected as unknown as string[], evidenceLinkOptions),
                                }}
                              >
                                {evidenceLinkOptions.map((option) => (
                                  <MenuItem key={option.key} value={getEvidenceLinkOptionValue(option)}>{option.label}</MenuItem>
                                ))}
                              </TextField>
                            </Stack>
                          </CardContent>
                        </Card>
                      )
                    })}
                  </Stack>
                ) : (
                  <Alert severity="info">No documents uploaded yet.</Alert>
                )}
              </Stack>
            ) : null}


            {tab === 6 ? (
              <Stack spacing={2}>
                <Typography variant="h6">Activity history</Typography>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Card variant="outlined" sx={{ flex: 1 }}>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">Visible events</Typography>
                      <Typography variant="h5">{audit.summary.totalEvents}</Typography>
                    </CardContent>
                  </Card>
                  <Card variant="outlined" sx={{ flex: 1 }}>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">Latest activity</Typography>
                      <Typography variant="body1">{formatDate(audit.summary.latestEventAt || undefined)}</Typography>
                    </CardContent>
                  </Card>
                </Stack>

                {audit.availableCategories.length > 0 ? (
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip
                      label={`All (${audit.overallSummary.totalEvents})`}
                      color={auditCategory === 'all' ? 'primary' : 'default'}
                      variant={auditCategory === 'all' ? 'filled' : 'outlined'}
                      onClick={() => setAuditCategory('all')}
                    />
                    {audit.availableCategories.map((category) => (
                      <Chip
                        key={category}
                        label={`${titleCase(category)} (${audit.overallSummary.byCategory[category] || 0})`}
                        color={auditCategory === category ? 'primary' : 'default'}
                        variant={auditCategory === category ? 'filled' : 'outlined'}
                        onClick={() => setAuditCategory(category)}
                      />
                    ))}
                  </Stack>
                ) : null}

                <TextField
                  label="Search activity"
                  value={auditSearch}
                  onChange={(event) => setAuditSearch(event.target.value)}
                  placeholder="Search action, details, actor, or metadata"
                  fullWidth
                />

                {auditQuery.isFetching ? <LinearProgress /> : null}

                {audit.events.length > 0 ? (
                  <Stack spacing={1}>
                    {audit.events.map((event) => (
                      <Card key={event.id} variant="outlined">
                        <CardContent>
                          <Stack spacing={1.25}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                              <Box>
                                <Typography variant="body1">{event.label}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {event.actor || 'System'} · {formatDate(event.at)}
                                </Typography>
                              </Box>
                              <Chip label={titleCase(event.category)} size="small" variant="outlined" />
                            </Stack>
                            {event.details ? (
                              <Typography variant="body2">{event.details}</Typography>
                            ) : null}
                            <Typography variant="caption" color="text.secondary">
                              {event.action}
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                ) : (
                  <Alert severity="info">
                    {auditSearch || auditCategory !== 'all'
                      ? 'No activity matches the current filters.'
                      : 'No activity yet.'}
                  </Alert>
                )}
              </Stack>
            ) : null}

            {tab === 4 ? (
              <Stack spacing={2}>
                <Typography variant="h6">Trial IR3 summary</Typography>
                <Alert severity="info">
                  This section turns what you entered into a trial IR3-style draft. Review it carefully: it is designed to help you and your accountant understand the likely return, not replace final professional review.
                </Alert>
                {review?.traceability ? (
                  <Card variant="outlined">
                    <CardContent>
                      <Stack spacing={1.25}>
                        <Typography variant="subtitle1">Reviewer traceability</Typography>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                          <Chip size="small" variant="outlined" color="success" label={`Evidenced ${review.traceability.evidencedFieldCount}/${review.traceability.keyFieldCount}`} />
                          <Chip size="small" variant="outlined" label={`Explained ${review.traceability.explainedFieldCount}/${review.traceability.keyFieldCount}`} />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          This highlights the key IR3 fields that already have explanation/source text and attached supporting evidence for accountant handoff.
                        </Typography>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                          {review.traceability.items.map((item) => (
                            <Chip
                              key={`trace-${item.ref}`}
                              size="small"
                              variant="outlined"
                              color={item.traceStatus === 'evidenced' ? 'success' : item.traceStatus === 'explained' ? 'default' : 'warning'}
                              label={`IR3 ${item.ref} · ${item.label} · ${item.evidenceCount} evidence`}
                            />
                          ))}
                        </Stack>
                        {review.traceability.gaps.length > 0 ? (
                          <Stack spacing={0.75}>
                            <Typography variant="caption" color="text.secondary">Reviewer follow-up</Typography>
                            {review.traceability.followUpPack?.headline ? (
                              <Typography variant="body2" color="text.secondary">
                                {review.traceability.followUpPack.headline}
                              </Typography>
                            ) : null}
                            {(review.traceability.followUpPack?.items?.length ? review.traceability.followUpPack.items : review.traceability.gaps).map((gap) => (
                              <Alert key={`trace-gap-${gap.ref}`} severity={gap.severity === 'high' ? 'error' : 'warning'}>
                                <strong>{`IR3 ${gap.ref} · ${gap.label}`}</strong> — {gap.reason}
                                {' '}
                                {'requestArea' in gap ? `Next request: ${gap.requestText} (${gap.requestArea}).` : ''}
                              </Alert>
                            ))}
                          </Stack>
                        ) : null}
                      </Stack>
                    </CardContent>
                  </Card>
                ) : null}
                {summaryItems.length > 0 ? (
                  <Stack spacing={1}>
                    {summaryItems.map(([ref, value]) => {
                      const fieldNote = fieldNotesByRef[ref] as { ref?: string; label?: string; note?: string; source?: string } | undefined
                      const friendlyLabel = fieldNote?.label || titleCase(ref)
                      const fieldEvidence = reviewEvidenceByIr3Ref[ref] || []

                      return (
                        <Card key={ref} variant="outlined">
                          <CardContent>
                            <Stack spacing={1.25}>
                              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                                <Box>
                                  <Typography variant="body1">{friendlyLabel}</Typography>
                                  <Typography variant="body2" color="text.secondary">{formatFieldValue(value)}</Typography>
                                </Box>
                                <Chip label={`IR3 ${ref}`} size="small" variant="outlined" />
                              </Stack>
                              {fieldNote?.note ? (
                                <Typography variant="body2" color="text.secondary">{fieldNote.note}</Typography>
                              ) : null}
                              {fieldEvidence.length > 0 ? (
                                <Stack spacing={0.75}>
                                  <Typography variant="caption" color="text.secondary">Supporting evidence mapped to this field</Typography>
                                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                    {fieldEvidence.map((item) => (
                                      <Chip
                                        key={`${ref}-${item.documentId}-${item.supports}`}
                                        size="small"
                                        variant="outlined"
                                        color={item.linkMode === 'manual' ? 'primary' : 'default'}
                                        label={`${item.linkMode === 'manual' ? 'Manual' : 'Auto'} · ${getIr3FieldEvidenceLabel(item)}`}
                                      />
                                    ))}
                                  </Stack>
                                </Stack>
                              ) : null}
                              {fieldNote?.source ? (
                                <Typography variant="caption" color="text.secondary">Where this came from: {fieldNote.source}</Typography>
                              ) : null}
                            </Stack>
                          </CardContent>
                        </Card>
                      )
                    })}
                  </Stack>
                ) : (
                  <Alert severity="info">No calculation data yet. Add income or crypto data first.</Alert>
                )}

                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>Adjustments and deductions</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Add extra items here if they were not already included elsewhere. These can change your draft refund or tax to pay.
                    </Typography>
                    <Stack spacing={2}>
                      <TextField label="Extra donations (NZD)" type="number" value={donationAmount} onChange={(e) => setDonationAmount(e.target.value)} helperText="Add any claimable donation amount not already captured in uploaded donation receipt totals." fullWidth />
                      <TextField label="PIE income (NZD)" type="number" value={pieIncome} onChange={(e) => setPieIncome(e.target.value)} helperText="Income from portfolio investment entities, usually shown on an annual tax certificate from your provider." fullWidth />
                      <TextField label="PIE credits (NZD)" type="number" value={pieTaxCredits} onChange={(e) => setPieTaxCredits(e.target.value)} helperText="Tax already paid within your PIE investment. Enter the credit amount from your annual statement." fullWidth />
                      <TextField label="Other tax paid (NZD)" type="number" value={extraTaxDeducted} onChange={(e) => setExtraTaxDeducted(e.target.value)} helperText="Use this when tax was already withheld outside PAYE and outside PIE, for example from another taxable payment." fullWidth />
                      <TextField label="Student loan paid (NZD)" type="number" value={studentLoanRepayments} onChange={(e) => setStudentLoanRepayments(e.target.value)} helperText="Add repayments already made through PAYE or separately if you need them reflected in this draft." fullWidth />
                      <Button variant="contained" onClick={handleSaveAdjustments} disabled={saveAdjustmentsMutation.isPending}>
                        {saveAdjustmentsMutation.isPending ? 'Saving…' : 'Save adjustments'}
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>

                {review ? (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>IR3 review context</Typography>
                      <Alert severity={review.readiness.status === 'strong' ? 'success' : review.readiness.status === 'review_needed' ? 'warning' : 'error'} sx={{ mb: 2 }}>
                        Readiness score: {review.readiness.score}/100 · {review.readiness.status}
                      </Alert>
                      {(review.warnings || []).length > 0 ? (
                        <Stack spacing={1} sx={{ mb: 2 }}>
                          <Typography variant="subtitle2">Detailed warning controls</Typography>
                          <Typography variant="body2" color="text.secondary">
                            These are the same underlying warnings as the task list above, but with evidence controls for deeper review.
                          </Typography>
                          {review.warnings.map((warning) => {
                            const overrideMode = warning.evidenceOverride?.mode === 'none'
                              ? 'none'
                              : warning.evidenceOverride?.mode === 'manual'
                                ? 'manual'
                                : 'auto'
                            const selectedDocumentIds = warning.evidenceOverride?.documentIds || []

                            return (
                              <Alert key={warning.code} severity={warning.severity === 'high' ? 'error' : 'warning'}>
                                <Stack spacing={1.25}>
                                  <Typography variant="body2">{warning.message}</Typography>
                                  {warning.evidence && warning.evidence.length > 0 ? (
                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                      {warning.evidence.map((item) => (
                                        <Chip
                                          key={`${warning.code}-${item.documentId}-${item.supports}`}
                                          size="small"
                                          variant="outlined"
                                          color={item.linkMode === 'manual' ? 'primary' : 'default'}
                                          label={`Evidence: ${getWarningEvidenceLabel(warning, item)}`}
                                        />
                                      ))}
                                    </Stack>
                                  ) : (
                                    <Typography variant="caption" color="text.secondary">No evidence currently attached to this warning.</Typography>
                                  )}
                                  <TextField
                                    select
                                    size="small"
                                    label="Evidence mode"
                                    value={overrideMode}
                                    onChange={(event) => {
                                      const mode = event.target.value as 'auto' | 'manual' | 'none'
                                      saveWarningEvidenceOverrideMutation.mutate({
                                        warningCode: warning.code,
                                        mode,
                                        documentIds: mode === 'manual' ? selectedDocumentIds : [],
                                      })
                                    }}
                                    disabled={saveWarningEvidenceOverrideMutation.isPending}
                                    helperText="Keep automatic evidence, hide it for this warning, or pick documents manually by warning code."
                                    fullWidth
                                  >
                                    <MenuItem value="auto">Automatic</MenuItem>
                                    <MenuItem value="manual">Manual document selection</MenuItem>
                                    <MenuItem value="none">No evidence for this warning</MenuItem>
                                  </TextField>
                                  {overrideMode === 'manual' ? (
                                    <TextField
                                      select
                                      size="small"
                                      label="Warning documents"
                                      value={selectedDocumentIds}
                                      onChange={(event) => {
                                        const documentIds = Array.isArray(event.target.value)
                                          ? event.target.value as string[]
                                          : [String(event.target.value)]
                                        saveWarningEvidenceOverrideMutation.mutate({
                                          warningCode: warning.code,
                                          mode: 'manual',
                                          documentIds,
                                        })
                                      }}
                                      disabled={saveWarningEvidenceOverrideMutation.isPending}
                                      helperText="These selected documents will be shown as manual evidence for this warning code."
                                      fullWidth
                                      SelectProps={{
                                        multiple: true,
                                        renderValue: (selected) => {
                                          const ids = selected as string[]
                                          if (ids.length === 0) return 'No documents selected yet'
                                          return ids
                                            .map((id) => docs.find((doc) => doc.id === id)?.originalName || id)
                                            .join(', ')
                                        },
                                      }}
                                    >
                                      {docs.map((doc) => (
                                        <MenuItem key={`${warning.code}-${doc.id}`} value={doc.id}>{doc.originalName}</MenuItem>
                                      ))}
                                    </TextField>
                                  ) : null}
                                </Stack>
                              </Alert>
                            )
                          })}
                        </Stack>
                      ) : null}
                      {review.summary?.provisionalTaxStatus ? (
                        <Stack spacing={1.25} sx={{ mb: review.summary?.studentLoanStatus?.hasStudentLoan || (review.assumptions || []).length > 0 ? 2 : 0 }}>
                          <Typography variant="subtitle2">Provisional tax visibility</Typography>
                          <Alert severity={review.summary.provisionalTaxStatus.relevant ? 'warning' : 'info'}>
                            <Stack spacing={0.75}>
                              <Typography variant="body2">
                                {review.summary.provisionalTaxStatus.relevant
                                  ? `Modeled residual income tax is ${formatFieldValue(review.summary.provisionalTaxStatus.modeledResidualIncomeTax)}, which is above the NZ$${review.summary.provisionalTaxStatus.threshold.toFixed(2)} threshold. The draft therefore surfaces provisional tax relevance and uses the standard option 5% uplift as a simple estimate basis.`
                                  : `Modeled residual income tax is ${formatFieldValue(review.summary.provisionalTaxStatus.modeledResidualIncomeTax)}, which is not above the NZ$${review.summary.provisionalTaxStatus.threshold.toFixed(2)} threshold. Provisional tax is not currently surfaced in this draft.`}
                              </Typography>
                              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                <Chip size="small" variant="outlined" label={`Residual income tax: ${formatFieldValue(review.summary.provisionalTaxStatus.modeledResidualIncomeTax)}`} color={review.summary.provisionalTaxStatus.relevant ? 'warning' : 'default'} />
                                <Chip size="small" variant="outlined" label={`Threshold: NZ$${review.summary.provisionalTaxStatus.threshold.toFixed(2)}`} />
                                <Chip size="small" variant="outlined" label={`Standard option estimate: ${formatFieldValue(review.summary.provisionalTaxStatus.estimatedStandardOptionTax)}`} color={review.summary.provisionalTaxStatus.relevant ? 'warning' : 'default'} />
                              </Stack>
                            </Stack>
                          </Alert>
                        </Stack>
                      ) : null}
                      {review.summary?.studentLoanStatus?.hasStudentLoan ? (
                        <Stack spacing={1.25} sx={{ mb: (review.assumptions || []).length > 0 ? 2 : 0 }}>
                          <Typography variant="subtitle2">Student loan treatment</Typography>
                          <Alert severity={review.summary.studentLoanStatus.status === 'ready' ? 'success' : review.summary.studentLoanStatus.status === 'partial' ? 'warning' : 'error'}>
                            <Stack spacing={0.75}>
                              <Typography variant="body2">
                                {review.summary.studentLoanStatus.status === 'ready'
                                  ? 'Student loan evidence and repayment amount are both present.'
                                  : review.summary.studentLoanStatus.status === 'partial'
                                    ? 'Student loan treatment is partly captured, but one piece is still missing.'
                                    : 'Student loan treatment still needs both supporting evidence and/or a repayment amount before the draft is reliable.'}
                              </Typography>
                              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                <Chip size="small" variant="outlined" label={review.summary.studentLoanStatus.hasStatement ? 'Statement uploaded' : 'Statement missing'} color={review.summary.studentLoanStatus.hasStatement ? 'success' : 'default'} />
                                <Chip size="small" variant="outlined" label={review.summary.studentLoanStatus.repaymentsEntered > 0 ? `Repayments entered: ${formatFieldValue(review.summary.studentLoanStatus.repaymentsEntered)}` : 'Repayments not entered yet'} color={review.summary.studentLoanStatus.repaymentsEntered > 0 ? 'success' : 'default'} />
                              </Stack>
                            </Stack>
                          </Alert>
                        </Stack>
                      ) : null}

                      <Alert severity="info" sx={{ mb: (review.assumptions || []).length > 0 ? 2 : 0 }}>
                        Accountant handoff is now the canonical home for readiness, reviewer action queue, sign-off, handoff, and closure confidence. Keep this section focused on the trial IR3 draft itself.
                      </Alert>
                      {(review.assumptions || []).length > 0 ? (
                        <Stack spacing={1}>
                          <Typography variant="subtitle2">Current assumptions</Typography>
                          {review.assumptions.map((assumption, index) => (
                            <Typography key={index} variant="body2" color="text.secondary">• {assumption}</Typography>
                          ))}
                        </Stack>
                      ) : null}
                    </CardContent>
                  </Card>
                ) : null}

                {reviewEvidence.length > 0 ? (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>Supporting evidence</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        These uploaded documents are currently linked to parts of your review so you can see what supports each area of the draft.
                      </Typography>
                      <Stack spacing={1.5}>
                        {reviewEvidence.map((item) => (
                          <Card key={`${item.documentId}-${item.supports}-${item.section}`} variant="outlined">
                            <CardContent>
                              <Stack spacing={1.25}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                                  <Box>
                                    <Typography variant="body1">{item.supports}</Typography>
                                    <Typography variant="body2" color="text.secondary">{item.document}</Typography>
                                  </Box>
                                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                    <Chip size="small" label={item.section} variant="outlined" />
                                    <Chip size="small" color={item.linkMode === 'manual' ? 'primary' : 'default'} variant="outlined" label={item.linkMode === 'manual' ? 'Manual link' : 'Auto link'} />
                                  </Stack>
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                  Linked from {titleCase(item.documentType)} · Added {formatDate(item.uploadedAt)}
                                </Typography>
                                {item.ir3Refs.length > 0 ? (
                                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                    {item.ir3Refs.map((ref) => (
                                      <Chip key={`${item.documentId}-${ref}`} size="small" label={`IR3 ${ref}`} />
                                    ))}
                                  </Stack>
                                ) : null}
                              </Stack>
                            </CardContent>
                          </Card>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                ) : null}

                {explanation ? (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>Summary for your accountant</Typography>
                      {explanation.headline ? (
                        <Alert severity="info" sx={{ mb: 2 }}>{explanation.headline}</Alert>
                      ) : null}
                      <Stack spacing={1}>
                        {(explanation.bullets || []).map((bullet, index) => (
                          <Typography key={index} variant="body2" color="text.secondary">• {bullet}</Typography>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                ) : null}

                {exportQuery.data ? (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>Files to send or review</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Use the options below depending on what you or your accountant need. The zip file is the main handoff pack. The accountant CSV is the clean form-mapping file. The detailed CSV includes the fuller review detail and traceability notes.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {exportQuery.data.pdf.title} · {formatDate(exportQuery.data.pdf.generatedAt)}
                      </Typography>
                      <Stack spacing={1} sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          JSON package includes workspace metadata, mapped values, calculated values, explanation, and review readiness.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Detailed CSV rows: {exportQuery.data.csv.split('\n').filter(Boolean).length} · Trial balance rows: {exportQuery.data.trialBalanceCsv.split('\n').filter(Boolean).length} · Accountant CSV rows: {exportQuery.data.accountantCsv.split('\n').filter(Boolean).length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          JSON generated: {formatDate(exportQuery.data.json.generatedAt)}
                        </Typography>
                      </Stack>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
                        <Button
                          variant="contained"
                          startIcon={<DownloadIcon />}
                          onClick={downloadAccountantPack}
                          disabled={isDownloadingAccountantPack}
                        >
                          {isDownloadingAccountantPack ? 'Preparing zip…' : 'Download accountant zip'}
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={<DownloadIcon />}
                          onClick={() => downloadTextFile(`accountant-import-${workspaceId || 'workspace'}.csv`, exportQuery.data.accountantCsv, 'text/csv;charset=utf-8')}
                        >
                          Download accountant CSV
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          onClick={() => downloadTextFile(`trial-balance-${workspaceId || 'workspace'}.csv`, exportQuery.data.trialBalanceCsv, 'text/csv;charset=utf-8')}
                        >
                          Download trial balance CSV
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          onClick={() => downloadTextFile(`ir3-detailed-review-${workspaceId || 'workspace'}.csv`, exportQuery.data.csv, 'text/csv;charset=utf-8')}
                        >
                          Download detailed review CSV
                        </Button>
                        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadPdfFile}>
                          Download PDF
                        </Button>
                        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadJsonFile}>
                          Download JSON
                        </Button>
                        <Button variant="text" startIcon={<ContentCopyIcon />} onClick={copyCsvToClipboard}>
                          Copy detailed review CSV
                        </Button>
                      </Stack>
                      {accountantPackFeedback ? <Alert severity={accountantPackFeedback.includes('Could not') ? 'warning' : 'success'} sx={{ mb: 2 }}>{accountantPackFeedback}</Alert> : null}
                      {copyFeedback ? <Alert severity={copyFeedback.includes('failed') ? 'warning' : 'success'} sx={{ mb: 2 }}>{copyFeedback}</Alert> : null}
                      <TextField value={exportQuery.data.csv} multiline minRows={8} fullWidth InputProps={{ readOnly: true }} />
                    </CardContent>
                  </Card>
                ) : null}
              </Stack>
            ) : null}

            {tab === finalReviewTabIndex ? (
              <Stack spacing={2}>
                <Typography variant="h6">Accountant handoff</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                  Use this tab when you want to see what is still open, confirm what will be handed over, and make sure your accountant receives a complete pack.
                </Typography>

                {reviewQuery.isLoading ? (
                  <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                    <Stack spacing={1} alignItems="center">
                      <CircularProgress size={24} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                        Loading handoff status...
                      </Typography>
                    </Stack>
                  </Box>
                ) : null}

                {reviewQuery.isError ? (
                  <Alert
                    severity="error"
                    action={
                      <Button color="inherit" size="small" onClick={() => reviewQuery.refetch()}>
                        Retry
                      </Button>
                    }
                  >
                    Could not load handoff data. {(reviewQuery.error as Error)?.message || 'Unknown error'}
                  </Alert>
                ) : null}

                {!reviewQuery.isLoading && !reviewQuery.isError && !reviewerActionQueue ? (
                  <Alert
                    severity="warning"
                    action={
                      <Button color="inherit" size="small" onClick={() => reviewQuery.refetch()}>
                        Reload
                      </Button>
                    }
                  >
                    Handoff details are not available yet for this workspace. Reload to fetch the latest review queue.
                  </Alert>
                ) : null}

                {reviewerActionQueue ? (
                  <Card variant="outlined">
                    <CardContent>
                      <Stack spacing={2}>
                        <Typography variant="subtitle1">1. Finish the remaining items</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                          {reviewerActionQueue.headline}
                          {reviewerActionQueue.closureSummary ? ` ${reviewerActionQueue.closureSummary}` : ''}
                        </Typography>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                          <Chip label={reviewerActionQueue.handoffStatus === 'ready_for_handoff' ? 'Handoff ready' : reviewerActionQueue.handoffStatus === 'blocked' ? 'Handoff blocked' : 'Review in progress'} variant="outlined" color={reviewerActionQueue.handoffStatus === 'ready_for_handoff' ? 'success' : reviewerActionQueue.handoffStatus === 'blocked' ? 'warning' : 'info'} />
                          <Chip label={`Open ${reviewerActionQueue.totalCount}`} variant="outlined" color={reviewerActionQueue.totalCount > 0 ? 'warning' : 'success'} />
                          <Chip label={`Resolved ${reviewerActionQueue.resolvedCount || 0}/${reviewerActionQueue.totalTrackedCount || reviewerActionQueue.resolvedCount || 0}`} variant="outlined" color={(reviewerActionQueue.resolvedCount || 0) > 0 ? 'success' : 'default'} />
                          <Chip label={`High priority ${reviewerActionQueue.highPriorityCount}`} variant="outlined" color={reviewerActionQueue.highPriorityCount > 0 ? 'error' : 'success'} />
                          {(reviewerActionQueue.categories || []).map((item) => (
                            <Chip key={`final-review-queue-cat-${item.category}`} label={`${item.label} ${item.count}`} size="small" variant="outlined" />
                          ))}
                        </Stack>
                        {reviewerActionQueue.handoffBlockers?.length ? (
                          <Card variant="outlined" sx={{ bgcolor: alpha('#fffdf8', 0.78), borderColor: alpha('#d97706', 0.18), boxShadow: 'none' }}>
                            <CardContent sx={{ p: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Still holding up the handoff</Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                                {reviewerActionQueue.handoffBlockers.slice(0, 3).join(' · ')}{reviewerActionQueue.handoffBlockers.length > 3 ? '…' : ''}
                              </Typography>
                            </CardContent>
                          </Card>
                        ) : null}
                        {reviewerActionQueue.remainingIssuesPack?.items?.length ? (
                          <Stack spacing={1}>
                            <Typography variant="subtitle2">{reviewerActionQueue.remainingIssuesPack.headline || 'Still to sort out'}</Typography>
                            {reviewerActionQueue.remainingIssuesPack.items.map((item) => (
                              <Card key={`final-review-remaining-${item.id}`} variant="outlined" sx={{ boxShadow: 'none', borderColor: alpha('#123244', 0.08) }}>
                                <CardContent sx={{ p: 2 }}>
                                  <Stack spacing={1}>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                                      <Typography variant="body2"><strong>{item.title}</strong></Typography>
                                      <Chip size="small" variant="outlined" color={item.severity === 'high' ? 'warning' : 'default'} label={item.severity === 'high' ? 'Do first' : 'Open'} />
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                                      {item.requestText} ({item.requestArea}).
                                    </Typography>
                                    {isEvidenceGapTitle(item.title) ? (
                                      <Button size="small" variant="outlined" onClick={() => setTab(3)} sx={{ alignSelf: 'flex-start' }}>
                                        Open Docs
                                      </Button>
                                    ) : null}
                                  </Stack>
                                </CardContent>
                              </Card>
                            ))}
                          </Stack>
                        ) : null}
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                            gap: 1.5,
                          }}
                        >
                          {reviewerActionQueue.handoffPack ? (
                            <Card variant="outlined" sx={{ boxShadow: 'none' }}>
                              <CardContent sx={{ p: 2 }}>
                                <Stack spacing={1}>
                                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                                    <Typography variant="subtitle2">2. Check what your accountant will receive</Typography>
                                    <Chip size="small" variant="outlined" color={reviewerActionQueue.handoffPack.status === 'ready' ? 'success' : 'default'} label={reviewerActionQueue.handoffPack.status === 'ready' ? 'Ready' : 'Needs a check'} />
                                  </Stack>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                                    {reviewerActionQueue.handoffPack.summary}
                                  </Typography>
                                  <Alert severity="info">
                                    Send the pack from this step. Step 4 is only where you record that the accountant or filing operator received it.
                                  </Alert>
                                  <Typography variant="caption" color="text.secondary">Next step: {reviewerActionQueue.handoffPack.nextStep}</Typography>
                                  {(reviewerActionQueue.handoffPack.checklist || []).slice(0, 3).map((item) => (
                                    <Typography key={`final-review-handoff-pack-${item.key}`} variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                                      • {item.label}: {item.detail}
                                    </Typography>
                                  ))}
                                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                    <Button
                                      variant="contained"
                                      size="small"
                                      startIcon={<DownloadIcon />}
                                      onClick={downloadAccountantPack}
                                      disabled={isDownloadingAccountantPack || !exportQuery.data}
                                    >
                                      {isDownloadingAccountantPack ? 'Preparing zip…' : 'Download accountant zip'}
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={openAccountantEmailDraft}
                                      disabled={!exportQuery.data}
                                    >
                                      Open email draft
                                    </Button>
                                  </Stack>
                                  {!exportQuery.data ? (
                                    <Typography variant="caption" color="text.secondary">
                                      The handoff export is still loading. When ready, use Download accountant zip to send the pack.
                                    </Typography>
                                  ) : null}
                                  {accountantPackFeedback ? <Alert severity={accountantPackFeedback.includes('Could not') ? 'warning' : 'success'}>{accountantPackFeedback}</Alert> : null}
                                </Stack>
                              </CardContent>
                            </Card>
                          ) : null}

                          {reviewerActionQueue.finalSignoff ? (
                            <Card variant="outlined" sx={{ boxShadow: 'none' }}>
                              <CardContent sx={{ p: 2 }}>
                                <Stack spacing={1}>
                                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                                    <Typography variant="subtitle2">3. Confirm it is ready to send</Typography>
                                    <Chip size="small" variant="outlined" color={reviewerActionQueue.finalSignoff.status === 'signed_off' ? 'success' : 'default'} label={reviewerActionQueue.finalSignoff.status === 'signed_off' ? 'Confirmed' : 'Pending'} />
                                  </Stack>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                                    {reviewerActionQueue.finalSignoff.summary}
                                  </Typography>
                                  {reviewerActionQueue.finalSignoff.signedOffAt || reviewerActionQueue.finalSignoff.signedOffBy ? (
                                    <Typography variant="caption" color="text.secondary">
                                      Confirmed at {reviewerActionQueue.finalSignoff.signedOffAt ? formatDate(reviewerActionQueue.finalSignoff.signedOffAt) : '—'} by {reviewerActionQueue.finalSignoff.signedOffBy || 'unknown reviewer'}
                                    </Typography>
                                  ) : null}
                                  {reviewerActionQueue.finalSignoff.recoveryStep ? (
                                    <Typography variant="caption" color="text.secondary">Recovery step: {reviewerActionQueue.finalSignoff.recoveryStep}</Typography>
                                  ) : null}
                                  {reviewerActionQueue.finalSignoff.status !== 'signed_off' ? (
                                      <Button
                                      variant="outlined"
                                      size="small"
                                      disabled={saveReviewerFinalSignoffMutation.isPending}
                                      onClick={() => {
                                        let overrideReason = ''
                                        if (reviewerActionQueue.handoffPack?.status !== 'ready') {
                                          overrideReason = window.prompt('This pack is not marked ready yet. Enter a reason if you still want to confirm it:', '') || ''
                                          if (!overrideReason.trim()) return
                                        }
                                        saveReviewerFinalSignoffMutation.mutate({ overrideReason: overrideReason.trim() || undefined })
                                      }}
                                      sx={{ alignSelf: 'flex-start' }}
                                    >
                                      Confirm ready to send
                                    </Button>
                                  ) : null}
                                </Stack>
                              </CardContent>
                            </Card>
                          ) : null}

                          {reviewerActionQueue.operatorHandoff ? (
                            <Card variant="outlined" sx={{ boxShadow: 'none' }}>
                              <CardContent sx={{ p: 2 }}>
                                <Stack spacing={1}>
                                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                                    <Typography variant="subtitle2">4. Confirm the handoff was received</Typography>
                                    <Chip size="small" variant="outlined" color={reviewerActionQueue.operatorHandoff.status === 'acknowledged' ? 'success' : 'default'} label={reviewerActionQueue.operatorHandoff.status === 'acknowledged' ? 'Received' : 'Pending'} />
                                  </Stack>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                                    {reviewerActionQueue.operatorHandoff.summary}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">Next step: {reviewerActionQueue.operatorHandoff.nextStep}</Typography>
                                  {reviewerActionQueue.operatorHandoff.acknowledgedAt || reviewerActionQueue.operatorHandoff.acknowledgedBy ? (
                                    <Typography variant="caption" color="text.secondary">
                                      Received at {reviewerActionQueue.operatorHandoff.acknowledgedAt ? formatDate(reviewerActionQueue.operatorHandoff.acknowledgedAt) : '—'} by {reviewerActionQueue.operatorHandoff.acknowledgedBy || 'unknown operator'}
                                    </Typography>
                                  ) : null}
                                  {reviewerActionQueue.operatorHandoff.status === 'pending_operator_ack' || reviewerActionQueue.operatorHandoff.status === 'stale_operator_handoff' ? (
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      disabled={saveOperatorHandoffAckMutation.isPending}
                                      onClick={() => {
                                        const note = window.prompt('Optional note confirming the handoff was received:', '') || ''
                                        saveOperatorHandoffAckMutation.mutate({ note: note.trim() || undefined })
                                      }}
                                      sx={{ alignSelf: 'flex-start' }}
                                    >
                                      Mark as received
                                    </Button>
                                  ) : null}
                                </Stack>
                              </CardContent>
                            </Card>
                          ) : null}

                          {reviewerActionQueue.filingExecution ? (
                            <Card variant="outlined" sx={{ boxShadow: 'none' }}>
                              <CardContent sx={{ p: 2 }}>
                                <Stack spacing={1}>
                                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                                    <Typography variant="subtitle2">5. Track what happens next</Typography>
                                    <Chip size="small" variant="outlined" color={reviewerActionQueue.filingExecution.status === 'confirmed' ? 'success' : reviewerActionQueue.filingExecution.status === 'submitted' ? 'info' : 'default'} label={filingCheckpointStatusLabel(reviewerActionQueue.filingExecution.status)} />
                                  </Stack>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                                    {reviewerActionQueue.filingExecution.summary}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">Next step: {reviewerActionQueue.filingExecution.nextStep}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {reviewerActionQueue.filingExecution.submittedAt ? `Submitted at ${formatDate(reviewerActionQueue.filingExecution.submittedAt)} · ` : ''}
                                    {reviewerActionQueue.filingExecution.confirmedAt ? `Confirmed at ${formatDate(reviewerActionQueue.filingExecution.confirmedAt)} · ` : ''}
                                    {reviewerActionQueue.filingExecution.irdReference ? `IRD ref ${reviewerActionQueue.filingExecution.irdReference}` : ''}
                                  </Typography>
                                  {!reviewerActionQueue.filingExecution.blockedByHandoff ? (
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                      {reviewerActionQueue.filingExecution.status === 'not_started' ? (
                                        <Button variant="outlined" size="small" disabled={saveFilingCheckpointMutation.isPending} onClick={() => saveFilingCheckpoint('submitted', reviewerActionQueue.filingExecution?.irdReference)}>
                                          Mark submitted
                                        </Button>
                                      ) : null}
                                      {reviewerActionQueue.filingExecution.status === 'submitted' ? (
                                        <Button variant="outlined" size="small" disabled={saveFilingCheckpointMutation.isPending} onClick={() => saveFilingCheckpoint('confirmed', reviewerActionQueue.filingExecution?.irdReference)}>
                                          Mark confirmed
                                        </Button>
                                      ) : null}
                                      {reviewerActionQueue.filingExecution.status !== 'not_started' ? (
                                        <Button variant="text" size="small" disabled={saveFilingCheckpointMutation.isPending} onClick={() => saveFilingCheckpoint('not_started')}>
                                          Reset filing
                                        </Button>
                                      ) : null}
                                    </Stack>
                                  ) : null}
                                </Stack>
                              </CardContent>
                            </Card>
                          ) : null}
                        </Box>

                        {reviewerActionQueue.handoffTimeline?.items?.length ? (
                          <Stack spacing={0.75}>
                            <Typography variant="subtitle2">Recent updates</Typography>
                            {reviewerActionQueue.handoffTimeline.items.slice(0, 3).map((item) => (
                              <Typography key={`final-review-handoff-timeline-${item.key}`} variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                                • {item.label}: {item.at ? formatDate(item.at) : '—'} by {item.by || 'unknown'}{item.detail ? ` — ${item.detail}` : ''}
                              </Typography>
                            ))}
                          </Stack>
                        ) : null}
                        {reviewerActionQueue.items.length > 0 ? (
                          <Stack spacing={1}>
                            {reviewerActionQueue.items.slice(0, 4).map((item) => (
                              <Card
                                key={`final-review-action-${item.id}`}
                                variant="outlined"
                                sx={{ boxShadow: 'none', borderColor: item.severity === 'high' ? alpha('#d97706', 0.28) : alpha('#123244', 0.08) }}
                              >
                                <CardContent sx={{ p: 2 }}>
                                  <Stack spacing={1}>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                                      <Typography variant="body2"><strong>{item.title}</strong></Typography>
                                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                        <Chip size="small" variant="outlined" label={reviewerActionSupportStateLabel(item.supportState)} />
                                        <Chip size="small" variant="outlined" label={reviewerActionTypeLabel(item.actionType)} />
                                      </Stack>
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>{item.detail}</Typography>
                                    <Typography variant="caption" color="text.secondary">Next step: {item.requestText} ({item.requestArea}).</Typography>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                      {isEvidenceGapTitle(item.title) ? (
                                        <Button size="small" variant="outlined" onClick={() => setTab(3)}>
                                          Open Docs
                                        </Button>
                                      ) : null}
                                      {item.actionLabel ? (
                                        <Button size="small" variant="outlined" onClick={() => setTab(blockerTargetTabIndex(item.targetTab))}>
                                          {item.actionLabel}
                                        </Button>
                                      ) : null}
                                      <Button
                                        size="small"
                                        variant="text"
                                        disabled={saveReviewerActionStatusMutation.isPending}
                                        onClick={() => saveReviewerActionStatusMutation.mutate({ actionId: item.id, status: 'resolved', title: item.title })}
                                      >
                                        Mark done
                                      </Button>
                                    </Stack>
                                  </Stack>
                                </CardContent>
                              </Card>
                            ))}
                          </Stack>
                        ) : (
                          <Stack spacing={0.75}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                              There are no open reviewer blockers right now.
                            </Typography>
                            {(reviewerActionQueue.finalSignoff?.status !== 'signed_off' || reviewerActionQueue.operatorHandoff?.status !== 'acknowledged') ? (
                              <Typography variant="caption" color="text.secondary">
                                You still need to run the handoff workflow below: download/send the accountant pack, confirm it is ready to send, and then mark receipt.
                              </Typography>
                            ) : null}
                          </Stack>
                        )}
                        {(reviewerActionQueue.recentlyResolved || reviewerActionQueue.resolvedItems || []).length > 0 ? (
                          <Stack spacing={1}>
                            <Typography variant="subtitle2">{reviewerActionQueue.recentlyResolvedHeadline || 'Recently completed'}</Typography>
                            {(reviewerActionQueue.recentlyResolved || reviewerActionQueue.resolvedItems || []).slice(0, 3).map((item) => (
                              <Card
                                key={`final-review-resolved-${item.id}`}
                                variant="outlined"
                                sx={{ boxShadow: 'none' }}
                              >
                                <CardContent sx={{ p: 2 }}>
                                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.25}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                                      <strong>{item.title}</strong> completed{item.resolvedAt ? ` at ${formatDate(item.resolvedAt)}` : ''}{item.resolutionNote ? ` — ${item.resolutionNote}` : ''}.
                                    </Typography>
                                    <Button
                                      size="small"
                                      variant="text"
                                      disabled={saveReviewerActionStatusMutation.isPending}
                                      onClick={() => saveReviewerActionStatusMutation.mutate({ actionId: item.id, status: 'open', title: item.title })}
                                      sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                                    >
                                      Mark open again
                                    </Button>
                                  </Stack>
                                </CardContent>
                              </Card>
                            ))}
                          </Stack>
                        ) : null}
                      </Stack>
                    </CardContent>
                  </Card>
                ) : null}

                {submissionReadiness ? (
                  <Card variant="outlined">
                    <CardContent>
                      <Stack spacing={2}>
                        <Typography variant="subtitle1">Ready for accountant review</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                          {submissionReadiness.status === 'ready_to_review'
                            ? 'There are no known blockers right now. This draft looks ready for final accountant review.'
                            : `${submissionReadiness.blockerCount} item${submissionReadiness.blockerCount === 1 ? '' : 's'} still need attention before this draft is ready to hand over.`}
                        </Typography>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                          <Chip
                            label={`Your information: ${submissionReadiness.questionnaire.answeredVisible}/${submissionReadiness.questionnaire.totalVisible}`}
                            color={submissionReadiness.questionnaire.complete ? 'success' : 'default'}
                            variant="outlined"
                          />
                          <Chip
                            label={`Documents: ${submissionReadiness.documents.receivedCount}/${submissionReadiness.documents.applicableCount}`}
                            color={submissionReadiness.documents.receivedCount >= submissionReadiness.documents.applicableCount && submissionReadiness.documents.applicableCount > 0 ? 'success' : 'default'}
                            variant="outlined"
                          />
                          <Chip
                            label={`High-severity warnings: ${highSeverityWarningCount}`}
                            color={highSeverityWarningCount > 0 ? 'error' : 'success'}
                            variant="outlined"
                          />
                        </Stack>
                        {submissionReadiness.blockers.length > 0 ? (
                          <Stack spacing={1}>
                            {submissionReadiness.blockers.slice(0, 4).map((blocker) => (
                              <Alert
                                key={blocker.code}
                                severity={blocker.severity === 'high' ? 'error' : 'warning'}
                                action={blocker.actionLabel ? (
                                  <Button color="inherit" size="small" onClick={() => setTab(blockerTargetTabIndex(blocker.targetTab))}>
                                    {blocker.actionLabel}
                                  </Button>
                                ) : undefined}
                              >
                                <Typography variant="body2"><strong>{blocker.label}:</strong> {blocker.message}</Typography>
                              </Alert>
                            ))}
                          </Stack>
                        ) : null}
                        {submissionReadiness.nextActions.length > 0 ? (
                          <Stack spacing={0.5}>
                            <Typography variant="subtitle2">Next actions</Typography>
                            {submissionReadiness.nextActions.slice(0, 3).map((action, index) => (
                              <Typography key={index} variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>• {action}</Typography>
                            ))}
                          </Stack>
                        ) : null}
                        {submissionReadiness.finalReviewChecklist?.length ? (
                          <Stack spacing={1}>
                            <Typography variant="subtitle2">Final checklist before handoff</Typography>
                            {submissionReadiness.finalReviewChecklist.map((item) => (
                              <Typography key={item.key} variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                                • {item.label}: {item.detail}
                              </Typography>
                            ))}
                          </Stack>
                        ) : null}
                      </Stack>
                    </CardContent>
                  </Card>
                ) : null}

                {reviewWarnings.length > 0 ? (
                  <Card variant="outlined">
                    <CardContent>
                      <Stack spacing={2}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
                          <Box>
                            <Typography variant="subtitle1">Items to review</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                              {highSeverityWarningCount > 0
                                ? `${highSeverityWarningCount} priority item${highSeverityWarningCount === 1 ? '' : 's'} still need attention.`
                                : `${reviewWarnings.length} item${reviewWarnings.length === 1 ? '' : 's'} still need review.`}
                            </Typography>
                          </Box>
                          <Button size="small" variant="outlined" onClick={() => setTab(5)} sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}>
                            Open trial IR3
                          </Button>
                        </Stack>
                        <Stack spacing={1}>
                          {reviewWarnings.slice(0, 3).map((warning, index) => (
                            <Card key={`${warning.code}-${index}`} variant="outlined" sx={{ boxShadow: 'none', borderColor: warning.severity === 'high' ? alpha('#d97706', 0.25) : alpha('#123244', 0.08) }}>
                              <CardContent sx={{ p: 2 }}>
                                <Stack spacing={1}>
                                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                                    <Typography variant="body2">{warning.message}</Typography>
                                    <Chip size="small" variant="outlined" color={warning.severity === 'high' ? 'warning' : 'default'} label={warning.severity === 'high' ? 'Do first' : 'Review'} />
                                  </Stack>
                                  {warning.evidence && warning.evidence.length > 0 ? (
                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                      {warning.evidence.map((item) => (
                                        <Chip
                                          key={`${warning.code}-${item.documentId}-${item.supports}`}
                                          size="small"
                                          variant="outlined"
                                          color={item.linkMode === 'manual' ? 'primary' : 'default'}
                                          label={`Evidence: ${getWarningEvidenceLabel(warning, item)}`}
                                        />
                                      ))}
                                    </Stack>
                                  ) : null}
                                </Stack>
                              </CardContent>
                            </Card>
                          ))}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: bodyFont }}>
                    There are no review items open right now.
                  </Typography>
                )}
              </Stack>
            ) : null}
          </CardContent>
        </Card>
      </Stack>
    </Container>
    </Box>
  )
}
